/**
 * Gossl Outfit Configurator v2
 * Fixed-position collage with proportional sizing and auto-grouping.
 * No dragging — curated magazine-style layout.
 *
 * ════════════════════════════════════════════════════════════════════
 *  KNOWN GOTCHAS — DO NOT REMOVE WITHOUT TESTING THE LISTED SCENARIO
 * ════════════════════════════════════════════════════════════════════
 *
 * These fixes have all been regressed at least once already. Before
 * deleting any code marked "// !! DO NOT REMOVE !!", verify the scenario
 * in the comment is still handled correctly. iOS Safari is the common
 * culprit — most of these were caught only on real iPhone testing.
 *
 *  1. iOS Safari faded share screenshot (commit 40e60d0, 2026-04-28)
 *     → showSharePreview() must pass `width / height / x / y /
 *       windowWidth` options to html2canvas, AND hide the actions
 *       buttons (NOT reposition the wrap). Removing any of these
 *       returns the faded-products bug.
 *
 *  2. Felix's _Artikel-Nr line item property (commit d156473, 2026-04-29)
 *     → Cart-add payload must include `properties._Artikel-Nr` from the
 *       product's `Artikelnr:` tag. Required by the Steyr/Bergheim
 *       store team to look up Intex articles in the Shopify backend.
 *
 *  3. /products/{handle}.json returns `available: null` for some products
 *     (cache / sales-channel quirk in Shopify).
 *     → Treat null/undefined as available — only mark sold-out on
 *       EXPLICIT `available === false`. Any check using `!v.available`
 *       falsely marks every variant of those products "ausverkauft".
 *
 *  4. Ensure window.Goessl is merged, never overwritten.
 *     (See goessl_window_goessl_merge memory.) Replacing the global
 *     wipes wishlist / quick-view / mega-menu hooks attached by other
 *     scripts.
 *
 *  5. canvas-item entrance animation (`canvasItemIn` 0.35s opacity 0→1)
 *     → Must complete BEFORE share screenshot capture. Currently we
 *       wait on image-load only — if a future change adds an animation
 *       lock, it should also be awaited.
 *
 * If you find yourself "cleaning up" any of the above, that means it
 * just looks redundant — not that it is. Run the configurator on a
 * real iPhone and try sharing before merging.
 * ════════════════════════════════════════════════════════════════════
 */
(function() {
  'use strict';

  var R2_UPLOAD_URL = 'https://goessl-r2-upload.g-ssl.workers.dev/upload';
  var SHORTLINK_CREATE_URL = 'https://share.goessl.com/s/create';
  var OG_CREATE_URL = 'https://share.goessl.com/o/create';
  var MAX_ITEMS = 7;

  // !! Brevo correction-email rescue (2026-04-30) — DO NOT REMOVE !!
  // The 2026-04-30 launch newsletter + correction email shipped a CTA
  // pointing at the bare configurator URL (no ?l= param), so clicks land
  // on an empty canvas instead of the showcased outfit shown in the
  // email image. When a bare URL arrives from a Brevo tracking domain,
  // redirect to the showcased outfit so the email image and the landing
  // page match. Safe to remove once the email click traffic dies down
  // (~2 weeks after send).
  (function brevoCorrectionRescue() {
    try {
      var hasParams = /[?&](l|o|oid|add)=/.test(window.location.search);
      if (hasParams) return;
      var ref = document.referrer || '';
      var fromBrevo = /(sendib|brevosend|sendinblue)/i.test(ref);
      if (!fromBrevo) return;
      var rescueParam = '?l=eyJnIjoiZiIsImkiOlt7ImgiOiI5NTA1OS1rbGFzc2lrLWRpcm5kbCIsImMiOiJmcmF1ZW4tZGlybmRsIiwidiI6NTUzMzkxMzk4OTE1MzQsInMiOjEwMDAsImNjIjoiNjEiLCJ4IjoyNTAsInkiOjY5LCJ3Ijo0OTIsIkgiOjgzNH0seyJoIjoiOTUwNzYtZGlybmRsYmx1c2UtbWl0LWhhbmRzdGljay1kZXRhaWwiLCJjIjoiZnJhdWVuLWRpcm5kbGJsdXNlbiIsInYiOjU5MTU2MDA4MDc1NTk4LCJzIjo4NTAsIngiOjAsInkiOjQ0MywidyI6Mzg3LCJIIjo1MDB9LHsiaCI6Ijk1MDY3LWJhcm9jay1kcnVja3NjaHVlcnplIiwiYyI6ImZyYXVlbi1zY2h1ZXJ6ZW4iLCJ2Ijo1OTEyMTI4MDU0OTE5OCwicyI6ODUwLCJ4IjoxLCJ5IjoxMywidyI6NDA0LCJIIjo2NzJ9LHsiaCI6IjkzOTk5LWxlZGVydGFzY2hlIiwiYyI6ImZyYXVlbi10YXNjaGVuIiwidiI6NTUzMjA1NzY1MjQ2MjIsInMiOjYwMCwieCI6NjYxLCJ5Ijo0NTgsInciOjI3NywiSCI6Mzg3fSx7ImgiOiI5MTU4NS1pc2NobGVyLXdhbGtqYWNrZSIsImMiOiJmcmF1ZW4tamFja2VuIiwidiI6NTUzMTkxNjQyNTY1OTAsInMiOjk1MCwieCI6NjAyLCJ5Ijo0NiwidyI6MzkxLCJIIjo2MTZ9XX0';
      window.location.replace(window.location.pathname + rescueParam);
    } catch (e) { /* fail open — never block the page */ }
  })();

  // Short-URL cache: long URL → Promise resolving to short URL.
  // Same long URL never gets minted twice; QR re-render reuses the resolved value.
  var shortUrlCache = {};

  function mintShortUrl(longUrl) {
    if (shortUrlCache[longUrl]) return shortUrlCache[longUrl];
    // The shortlink endpoint is a Cloudflare Worker — cold starts plus CORS
    // preflight regularly take 5-7s on first hit, then drop to <1s once the
    // Worker is warm. We give the fetch 10s before giving up. Retry once on
    // failure; the second attempt almost always succeeds because the Worker
    // is now warm.
    function attempt(attemptNo) {
      var controller = new AbortController();
      var timer = setTimeout(function () { controller.abort(); }, 10000);
      return fetch(SHORTLINK_CREATE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: longUrl }),
        signal: controller.signal
      }).then(function (r) {
        clearTimeout(timer);
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      }).then(function (data) {
        if (data && data.ok && data.shortUrl) return data.shortUrl;
        throw new Error((data && data.error) || 'shortlink_failed');
      }).catch(function (err) {
        clearTimeout(timer);
        if (attemptNo < 2) {
          if (window.console) {
            console.warn('[outfit-configurator] short-URL mint attempt ' + attemptNo + ' failed (' +
              (err && err.message ? err.message : err) + ') — retrying.');
          }
          return attempt(attemptNo + 1);
        }
        if (window.console) {
          console.warn('[outfit-configurator] short-URL mint failed twice:',
            err && err.message ? err.message : err,
            '— QR will encode long URL (denser, but still scans).');
        }
        delete shortUrlCache[longUrl]; // allow retry on next outfit change
        return longUrl;
      });
    }
    shortUrlCache[longUrl] = attempt(1);
    return shortUrlCache[longUrl];
  }

  // OG-aware share URL: gsl.asksj.com/o/CODE that, when crawled by Facebook /
  // WhatsApp / Twitter / etc., returns HTML with the outfit screenshot as
  // og:image. Humans hitting the same URL get a 302 to the goessl.com
  // configurator with the outfit pre-loaded. Used for FB + Pinterest shares
  // so the link preview shows the actual outfit.
  function mintOgUrl(longUrl, imageUrl) {
    return fetch(OG_CREATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        url: longUrl,
        image: imageUrl,
        title: 'Mein Gössl Outfit',
        description: 'Schauen Sie sich mein Gössl Outfit an.'
      })
    }).then(function(r) { return r.json(); }).then(function(data) {
      if (data && data.ok && data.shortUrl) return data.shortUrl;
      throw new Error((data && data.error) || 'og_url_failed');
    }).catch(function() {
      // On failure, return the long URL — share still works, just no rich preview.
      return longUrl;
    });
  }

  // Color family swatch hexes — kept in lockstep with the collection filter panel
  // (see .goessl-filter__color-swatch[data-color-family=...] in main-collection.liquid).
  // Key = first digit of Gössl color code; value = swatch hex used in the collection filters.
  var COLOR_FAMILIES = {
    '1': '#FFFFFF',  // Weiss
    '2': '#E8DCC8',  // Natur
    '3': '#C41E3A',  // Rot
    '4': '#2D5016',  // Grün
    '5': '#1B3A6B',  // Blau
    '6': '#E8A0BF',  // Rosa
    '7': '#333333',  // Grau & Schwarz
    '8': '#8B5E3C',  // Braun
    '9': '#C5A572',  // Gelb & Gold
    '0': '#aaaaaa'   // Sonstige (no equivalent in collection filter — fallback grey)
  };

  function getColorHex(colorCode) {
    if (!colorCode) return '#ccc';
    var first = String(colorCode).charAt(0);
    return COLOR_FAMILIES[first] || '#ccc';
  }

  function extractColorCode(optionValue) {
    if (!optionValue) return null;
    var match = optionValue.match(/\s(\d+)$/);
    return match ? match[1] : null;
  }

  // Find the first image matching a color code by parsing the filename.
  // Intex image naming: {article_parts}-{colorCode}-{position}_{hash}.ext
  //   position: -10 = front, -11 = back, -12..-18 = details, -19 = alt
  // We prefer the -10 (front) for the matched color, then any matching
  // image, then null. This guards against products whose Shopify image
  // order has the front image not at index 0 (e.g. 95054-walkjacke).
  function findImageForColor(product, colorCode) {
    if (!product.images || !colorCode) return null;
    var pattern = '-' + colorCode + '-';
    var firstMatch = null;
    var frontMatch = null;
    for (var i = 0; i < product.images.length; i++) {
      var src = product.images[i].src;
      if (src.indexOf(pattern) === -1) continue;
      if (!firstMatch) firstMatch = product.images[i];
      // Match -{color}-10 (front), allowing for either filename end or hash suffix
      if (src.indexOf(pattern + '10') > -1) { frontMatch = product.images[i]; break; }
    }
    return frontMatch || firstMatch;
  }

  // Pick the best card image for a product. Intex naming convention:
  //   -10 = front (preferred)
  //   -11 = back (centered, decent fallback)
  //   -19 = alternate full-body
  //   -12..-18 = details (often cropped/zoomed — last resort)
  // We walk the ladder in that order so we never show a detail shot
  // when a front/back/alt is available, even if upstream image
  // ordering puts the detail at index 0 (e.g. 95054-walkjacke,
  // which has no -10 at all).
  function findFrontImage(product) {
    if (!product.images || !product.images.length) return null;
    var matchSuffix = function(suffix) {
      var re = new RegExp('-\\d{1,4}-' + suffix + '[._]');
      for (var i = 0; i < product.images.length; i++) {
        if (re.test(product.images[i].src)) return product.images[i];
      }
      return null;
    };
    return matchSuffix('10') || matchSuffix('11') || matchSuffix('19') || product.images[0];
  }

  // Size ordering (matches product-form.js)
  var SIZE_ORDER = [
    'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL',
    '0', '1', '2', '3', '4', '4+', '5', '5+', '6', '6+',
    '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36', '37', '38',
    '39', '40', '41', '42', '44', '46', '48', '50', '52', '54', '56', '58', '60',
    '62', '64', '86', '92', '98', '104', '110', '116', '122', '128', '134', '140',
    '146', '152', '158', '164', '170', '176'
  ];

  // ═══════════════════════════════════════════
  //  CATEGORY SIZES + DYNAMIC GRID LAYOUTS
  // ═══════════════════════════════════════════

  // Size scale per category (1.0 = full cell, smaller = scaled down within cell)
  var CATEGORY_SIZES = {
    'dirndl': 1.0, 'kleider': 1.0, 'roecke': 0.85,
    'lederhosen': 1.0, 'hosen': 0.9,
    'blusen': 0.85, 'dirndlblusen': 0.85, 'hemden': 0.85,
    'jacken': 0.95, 'maentel': 0.95, 'strick': 0.85, 'westen': 0.85, 'gilets': 0.85,
    'schuerzen': 0.85, 'tuecher-schals': 0.6, 'krawatten': 0.5, 'stecktucher': 0.5,
    'schuhe': 0.55, 'stutzen': 0.5,
    'taschen': 0.6, 'huete': 0.55, 'accessoires': 0.5, 'guertel': 0.45
  };

  function getCategorySize(collection) {
    var subcat = collection.replace(/^(frauen|maenner|kinder)-/, '');
    return CATEGORY_SIZES[subcat] || 0.7;
  }

  // Grid positions for N items. Cells overlap ~4% for tight nestling.
  // Items sorted by size — position[0] is always the hero (biggest item).
  // Each cell: { x, y, w, h } as fractions of canvas.
  var GRID_LAYOUTS = {
    1: [
      { x: 0.25, y: 0.02, w: 0.50, h: 0.90 }
    ],
    2: [
      { x: 0.20, y: 0.02, w: 0.45, h: 0.90 },
      { x: 0.50, y: 0.08, w: 0.45, h: 0.84 }
    ],
    // Diagonal: hero center, #2 upper-left, #3 lower-right
    3: [
      { x: 0.28, y: 0.08, w: 0.44, h: 0.76 },
      { x: 0.00, y: 0.00, w: 0.38, h: 0.52 },
      { x: 0.58, y: 0.40, w: 0.42, h: 0.56 }
    ],
    // 2x2 stagger: hero top-left, #2 top-right offset down, #3 bottom-left, #4 bottom-right offset
    4: [
      { x: 0.00, y: 0.00, w: 0.46, h: 0.56 },
      { x: 0.50, y: 0.06, w: 0.46, h: 0.54 },
      { x: 0.04, y: 0.46, w: 0.44, h: 0.54 },
      { x: 0.52, y: 0.50, w: 0.44, h: 0.50 }
    ],
    // 3 top + 2 bottom, overlapping
    5: [
      { x: 0.28, y: 0.00, w: 0.44, h: 0.62 },
      { x: 0.00, y: 0.02, w: 0.36, h: 0.50 },
      { x: 0.62, y: 0.02, w: 0.38, h: 0.50 },
      { x: 0.04, y: 0.44, w: 0.38, h: 0.56 },
      { x: 0.55, y: 0.46, w: 0.42, h: 0.54 }
    ],
    // 3x2 stagger with overlap
    6: [
      { x: 0.28, y: 0.00, w: 0.40, h: 0.58 },
      { x: 0.00, y: 0.00, w: 0.36, h: 0.54 },
      { x: 0.62, y: 0.00, w: 0.38, h: 0.54 },
      { x: 0.00, y: 0.46, w: 0.36, h: 0.54 },
      { x: 0.30, y: 0.50, w: 0.38, h: 0.50 },
      { x: 0.62, y: 0.46, w: 0.38, h: 0.54 }
    ],
    // 4 top + 3 bottom, staggered
    7: [
      { x: 0.18, y: 0.00, w: 0.34, h: 0.52 },
      { x: 0.00, y: 0.02, w: 0.26, h: 0.46 },
      { x: 0.44, y: 0.02, w: 0.28, h: 0.46 },
      { x: 0.68, y: 0.00, w: 0.32, h: 0.48 },
      { x: 0.04, y: 0.42, w: 0.34, h: 0.56 },
      { x: 0.32, y: 0.46, w: 0.34, h: 0.52 },
      { x: 0.60, y: 0.42, w: 0.36, h: 0.56 }
    ],
    // 4 top + 4 bottom, staggered
    8: [
      { x: 0.18, y: 0.00, w: 0.32, h: 0.50 },
      { x: 0.00, y: 0.02, w: 0.26, h: 0.44 },
      { x: 0.44, y: 0.02, w: 0.26, h: 0.44 },
      { x: 0.68, y: 0.00, w: 0.30, h: 0.46 },
      { x: 0.04, y: 0.42, w: 0.28, h: 0.52 },
      { x: 0.26, y: 0.46, w: 0.28, h: 0.48 },
      { x: 0.48, y: 0.42, w: 0.28, h: 0.52 },
      { x: 0.70, y: 0.44, w: 0.30, h: 0.50 }
    ]
  };

  // ═══════════════════════════════════════════
  //  STATE
  // ═══════════════════════════════════════════

  var productCache = {};
  // Unified canvas state — items from any gender (Frauen/Männer/Kinder) live in one composition
  var canvasState = [];
  // currentGender is derived from the most-recently-selected category prefix.
  // Kept for save metadata + share-URL back-compat. UI no longer exposes a gender toggle.
  var currentGender = 'frauen';
  // Set when the page is opened against a saved outfit (?oid=…) so subsequent
  // saves update that record instead of creating a new one.
  var currentOutfitId = null;
  var undoStack = []; // snapshots of state for undo
  var MAX_UNDO = 15;

  // Color family labels — must match the collection filter panel's data-color-family values
  var COLOR_FAMILY_NAMES = {
    '1': 'Weiss', '2': 'Natur', '3': 'Rot', '4': 'Grün',
    '5': 'Blau', '6': 'Rosa', '7': 'Grau & Schwarz', '8': 'Braun',
    '9': 'Gelb & Gold', '0': 'Sonstige'
  };

  // DOM refs
  var canvasItems, canvasEmpty, totalEl, saveBtn, cartBtn, shareBtn, stickyFooter, resetBtn, clearBtn, undoBtn;
  var productGrid; // shared product grid element
  var filterBar; // filter chips container
  var activeFilters = []; // active color family codes
  var gridCache = {}; // collection → DocumentFragment cache
  // Selected-items panel refs
  var selectedList, selectedCount, selectedTotal, selectedCart, selectedEmpty;

  function getState() { return canvasState; }
  function setState(items) { canvasState = items; }

  // ═══════════════════════════════════════════
  //  INIT
  // ═══════════════════════════════════════════

  function init() {
    canvasItems = document.querySelector('[data-canvas-items]');
    canvasEmpty = document.querySelector('[data-canvas-empty]');
    totalEl = document.querySelector('[data-outfit-total]');
    saveBtn = document.querySelector('[data-outfit-save]');
    shareBtn = document.querySelector('[data-outfit-share]');
    cartBtn = document.querySelector('[data-outfit-cart]');
    stickyFooter = document.querySelector('[data-outfit-footer]');
    productGrid = document.querySelector('[data-product-grid]');
    filterBar = document.querySelector('[data-outfit-filters]');
    resetBtn = document.querySelector('[data-reset-positions]');
    clearBtn = document.querySelector('[data-clear-canvas]');
    selectedList = document.querySelector('[data-selected-list]');
    selectedCount = document.querySelector('[data-selected-count]');
    selectedTotal = document.querySelector('[data-selected-total]');
    selectedCart = document.querySelector('[data-selected-cart]');

    if (!canvasItems) return;

    if (selectedCart) {
      selectedCart.addEventListener('click', function() {
        if (!getState().length) {
          showNotice('Canvas ist leer', 'Bitte fügen Sie zuerst Stücke aus den Kategorien hinzu.');
          return;
        }
        var allSized = getState().every(function(it) { return it.variant_id; });
        if (allSized) {
          submitInlineCart();
        } else {
          openSizeModal();
        }
      });
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', function() { resetPositions(); });
    }
    if (clearBtn) {
      clearBtn.addEventListener('click', function() { clearCanvas(); });
    }
    undoBtn = document.querySelector('[data-undo-canvas]');
    if (undoBtn) {
      undoBtn.addEventListener('click', function() { undo(); });
    }

    // QR is intentionally NOT draggable (per SJ): it's a fixed branded
    // element pinned to the bottom-centre of the canvas. Same for the
    // brand mark at the top-centre. Both must stay anchored so the
    // share screenshot is always composed correctly.

    // Generate initial QR code with current share URL
    updateCanvasQR();

    // Click empty canvas space to deselect all items
    var canvasEl = document.querySelector('[data-outfit-canvas]');
    if (canvasEl) {
      canvasEl.addEventListener('click', function(e) {
        if (!e.target.closest('.outfit-cfg__canvas-item')) {
          canvasItems.querySelectorAll('.outfit-cfg__canvas-item.is-tapped').forEach(function(c) {
            c.classList.remove('is-tapped');
          });
        }
      });
    }

    bindGenderToggle();
    bindCategorySelects();
    bindBottomBar();
    loadFromUrl();

    // Auto-load first category from the combined select
    var firstSelect = document.querySelector('.outfit-cfg__cat-select');
    if (firstSelect && firstSelect.value) showProductGrid(firstSelect.value);
  }

  // ═══════════════════════════════════════════
  //  GENDER TOGGLE — removed; gender is now derived from selected category
  // ═══════════════════════════════════════════

  function bindGenderToggle() {
    // No-op: the gender toggle UI was removed in favour of a single combined dropdown.
    // currentGender is updated whenever a category is selected (see bindCategorySelects).
  }

  // ═══════════════════════════════════════════
  //  CATEGORY DROPDOWN — single combined select with optgroups
  // ═══════════════════════════════════════════

  function bindCategorySelects() {
    document.querySelectorAll('.outfit-cfg__cat-select').forEach(function(select) {
      select.addEventListener('change', function() {
        // Derive currentGender from the selected category's prefix
        if (select.value.indexOf('frauen-') === 0) currentGender = 'frauen';
        else if (select.value.indexOf('maenner-') === 0) currentGender = 'maenner';
        else if (select.value.indexOf('kinder-') === 0) currentGender = 'kinder';
        showProductGrid(select.value);
      });
    });
  }

  function showProductGrid(collection) {
    if (!productGrid) return;

    // Reset filters when switching categories
    activeFilters = [];

    // Check cache
    if (gridCache[collection]) {
      productGrid.replaceChildren();
      gridCache[collection].forEach(function(card) {
        card.hidden = false;
        productGrid.appendChild(card);
      });
      // Rebuild filters from cached products
      var cachedProducts = gridCache[collection].map(function(card) {
        return productCache[card.dataset.handle];
      }).filter(Boolean);
      buildFilters(cachedProducts);
      highlightSelectedCards();
      applyFilters();
      return;
    }

    // Show loading
    productGrid.replaceChildren();
    var loading = document.createElement('p');
    loading.className = 'outfit-cfg__loading';
    loading.textContent = 'Wird geladen...';
    productGrid.appendChild(loading);

    // Two parallel fetches:
    //   1. products.json → product data (price, images, variants, etc.)
    //   2. collection HTML → display order (Shopify's products.json ignores
    //      the collection's manual sort, always returns created-desc).
    // Then reorder the products from #1 to match the handle sequence in #2,
    // so the configurator grid mirrors what the customer sees on the live
    // collection page (pinned-top → newest+best → sale-bottom from task #161).
    Promise.all([
      fetch('/collections/' + collection + '/products.json?limit=48').then(function(r) { return r.ok ? r.json() : null; }),
      fetch('/collections/' + collection).then(function(r) { return r.ok ? r.text() : ''; }).catch(function() { return ''; })
    ]).then(function(results) {
      var data = results[0];
      var html = results[1];
      if (!data || !data.products || !data.products.length) {
        productGrid.replaceChildren();
        var empty = document.createElement('p');
        empty.className = 'outfit-cfg__loading';
        empty.textContent = 'Keine Produkte gefunden.';
        productGrid.appendChild(empty);
        return;
      }

      // Build {handle -> { weight, onSale, idx }} from the product cards in
      // the HTML. Two important details:
      //
      // 1. SCOPE the regex to <article class="goessl-product-card"> blocks —
      //    a bare /\/products\/.../ regex picks up the mega-menu featured
      //    product (which appears BEFORE the grid in source order) and ranks
      //    it first in the configurator. Only the actual cards belong here.
      //
      // 2. The live collection page applies a client-side resort
      //    (sale-to-bottom, then season-weight DESC) AFTER initial render.
      //    The SSR card sequence is just Shopify's underlying collection
      //    order — to match what users actually see we have to apply the
      //    same resort using each card's data-on-sale + data-season-weight.
      var orderMap = {};
      if (html) {
        var cardPattern = /<article[^>]*class="[^"]*goessl-product-card[^"]*"([\s\S]*?)<\/article>/g;
        var cardMatch;
        var cardIdx = 0;
        while ((cardMatch = cardPattern.exec(html)) !== null) {
          var card = cardMatch[0];
          var hrefMatch = card.match(/\/products\/([a-z0-9-]+)/);
          if (!hrefMatch) continue;
          var h = hrefMatch[1];
          if (h in orderMap) continue;
          var weightMatch = card.match(/data-season-weight="(\d+)"/);
          var saleMatch = card.match(/data-on-sale="(true|false)"/);
          orderMap[h] = {
            weight: weightMatch ? parseInt(weightMatch[1], 10) : 0,
            onSale: saleMatch ? saleMatch[1] === 'true' : false,
            idx: cardIdx++
          };
        }
      }

      // Stable resort: matches collection-page sort exactly.
      //   1. on-sale → bottom
      //   2. higher season-weight first
      //   3. fall back to source order (idx)
      // Products not in the SSR snapshot (newer than the page) go to the end.
      data.products.sort(function(a, b) {
        var ma = orderMap[a.handle];
        var mb = orderMap[b.handle];
        if (!ma && !mb) return 0;
        if (!ma) return 1;
        if (!mb) return -1;
        if (ma.onSale !== mb.onSale) return ma.onSale ? 1 : -1;
        if (ma.weight !== mb.weight) return mb.weight - ma.weight;
        return ma.idx - mb.idx;
      });

      var cards = [];
      data.products.forEach(function(product) {
        // Skip products with no images — the configurator is image-driven, an empty card is broken UX
        if (!product.images || !product.images.length) return;
        productCache[product.handle] = product;
        cards.push(createMiniCard(product, collection));
      });
      gridCache[collection] = cards;
      productGrid.replaceChildren();
      cards.forEach(function(card) {
        productGrid.appendChild(card);
      });
      buildFilters(data.products);
      highlightSelectedCards();
      applyFilters();
    }).catch(function() {
      productGrid.replaceChildren();
      var err = document.createElement('p');
      err.className = 'outfit-cfg__loading';
      err.textContent = 'Fehler beim Laden.';
      productGrid.appendChild(err);
    });
  }

  function highlightSelectedCards() {
    if (!productGrid) return;
    var handles = {};
    getState().forEach(function(item) { handles[item.handle] = true; });
    productGrid.querySelectorAll('.goessl-product-card').forEach(function(card) {
      card.classList.toggle('is-selected', !!handles[card.dataset.handle]);
    });
  }

  // ═══════════════════════════════════════════
  //  COLOR FILTERS
  // ═══════════════════════════════════════════

  function buildFilters(products) {
    if (!filterBar) return;

    // Collect unique color families from loaded products
    var families = {};
    products.forEach(function(product) {
      (product.variants || []).forEach(function(v) {
        var code = extractColorCode(v.option1);
        if (code) {
          var family = code.charAt(0);
          families[family] = true;
        }
      });
    });

    // Always show the full family palette (matches the collection filter sidebar).
    // Families that aren't present in this category are still rendered, with a
    // dimmed/disabled appearance via .is-empty so the row stays consistent.
    var ALL_FAMILIES = ['1','2','3','4','5','6','7','8','9'];
    var familyKeys = ALL_FAMILIES.slice();
    var familyHasProducts = function(k) { return !!families[k]; };
    if (familyKeys.length < 2) {
      filterBar.hidden = true;
      return;
    }

    // Clear existing chips (keep "Alle")
    var allBtn = filterBar.querySelector('[data-filter-all]');
    filterBar.replaceChildren();
    if (allBtn) {
      filterBar.appendChild(allBtn);
    } else {
      allBtn = document.createElement('button');
      allBtn.type = 'button';
      allBtn.className = 'outfit-cfg__filter-chip is-active';
      allBtn.dataset.filterAll = '';
      allBtn.textContent = 'Alle';
      filterBar.appendChild(allBtn);
    }

    allBtn.addEventListener('click', function() {
      activeFilters = [];
      filterBar.querySelectorAll('.outfit-cfg__filter-chip').forEach(function(c) {
        c.classList.remove('is-active');
      });
      allBtn.classList.add('is-active');
      applyFilters();
    });

    familyKeys.forEach(function(family) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'outfit-cfg__filter-chip';
      chip.dataset.filterFamily = family;

      var dot = document.createElement('span');
      dot.className = 'outfit-cfg__filter-dot';
      dot.style.backgroundColor = COLOR_FAMILIES[family] || '#ccc';
      chip.appendChild(dot);

      // Bubble-only — name shown as native title tooltip
      chip.title = COLOR_FAMILY_NAMES[family] || family;
      chip.setAttribute('aria-label', COLOR_FAMILY_NAMES[family] || family);

      // Dim chips for families with no products in this category (still clickable but visually muted)
      if (!familyHasProducts(family)) chip.classList.add('is-empty');

      chip.addEventListener('click', function() {
        var idx = activeFilters.indexOf(family);
        if (idx > -1) {
          activeFilters.splice(idx, 1);
          chip.classList.remove('is-active');
        } else {
          activeFilters.push(family);
          chip.classList.add('is-active');
        }

        // Update "Alle" state
        allBtn.classList.toggle('is-active', activeFilters.length === 0);
        applyFilters();
      });

      filterBar.appendChild(chip);
    });

    activeFilters = [];
    filterBar.hidden = false;
  }

  // ═══════════════════════════════════════════
  //  LENGTH COMPATIBILITY
  // ═══════════════════════════════════════════

  // Extract dirndl-length:XX tags from a product's tag string
  function getDirndlLength(product) {
    if (!product || !product.tags) return null;
    var tags = typeof product.tags === 'string' ? product.tags.split(', ') : product.tags;
    for (var i = 0; i < tags.length; i++) {
      var match = tags[i].match(/^dirndl-length[:\-](\d+)/i);
      if (match) return match[1];
    }
    return null;
  }

  // Check if a product is length-compatible with items on canvas.
  // Only applies to Schürzen + Dirndlkleider — all other categories always pass.
  function isLengthCompatible(product, currentCollection) {
    var subcat = currentCollection.replace(/^(frauen|maenner|kinder)-/, '');
    // Only filter length-sensitive categories
    var lengthSensitive = ['dirndl', 'schuerzen', 'kleider', 'roecke'];
    if (lengthSensitive.indexOf(subcat) === -1) return true;

    var productLength = getDirndlLength(product);
    if (!productLength) return true; // No length tag = show it

    // Check canvas items for a length constraint
    var state = getState();
    for (var i = 0; i < state.length; i++) {
      var canvasProduct = productCache[state[i].handle];
      if (!canvasProduct) continue;
      var canvasLength = getDirndlLength(canvasProduct);
      if (canvasLength && canvasLength !== productLength) return false;
    }
    return true;
  }

  function applyFilters() {
    if (!productGrid) return;
    var cards = productGrid.querySelectorAll('.goessl-product-card');
    // Get current collection from dropdown
    var currentSelect = document.querySelector('.outfit-cfg__cat-select-wrap:not([hidden]) .outfit-cfg__cat-select');
    var currentCollection = currentSelect ? currentSelect.value : '';

    if (activeFilters.length === 0) {
      // Show all, but still apply length compatibility
      cards.forEach(function(card) {
        var product = productCache[card.dataset.handle];
        card.hidden = product ? !isLengthCompatible(product, currentCollection) : false;
        if (!card.hidden) resetCardImage(card);
      });
      return;
    }

    cards.forEach(function(card) {
      var handle = card.dataset.handle;
      var product = productCache[handle];
      if (!product) { card.hidden = false; return; }

      // Find first variant matching any active color family filter
      var matchingCode = null;
      (product.variants || []).forEach(function(v) {
        if (matchingCode) return;
        var code = extractColorCode(v.option1);
        if (code && activeFilters.indexOf(code.charAt(0)) > -1) {
          matchingCode = code;
        }
      });

      if (matchingCode && isLengthCompatible(product, currentCollection)) {
        card.hidden = false;
        // Swap card image to the matching color
        var colorImg = findImageForColor(product, matchingCode);
        var cardImg = card.querySelector('.goessl-product-card__img');
        if (colorImg && cardImg) {
          cardImg.src = colorImg.src.replace(/\.([^.]+)$/, '_300x.$1');
        }
        // Update swatch active state
        card.querySelectorAll('.goessl-product-card__swatch').forEach(function(s) {
          s.classList.toggle('is-active', s.dataset.colorCode === matchingCode);
        });
        selectedColors[handle] = matchingCode;
      } else {
        card.hidden = true;
      }
    });
  }

  // Reset card image to its original (first) image
  function resetCardImage(card) {
    var handle = card.dataset.handle;
    var product = productCache[handle];
    if (!product || !product.images || !product.images[0]) return;
    var cardImg = card.querySelector('.goessl-product-card__img');
    if (cardImg) {
      cardImg.src = product.images[0].src.replace(/\.([^.]+)$/, '_300x.$1');
    }
    // Reset swatch to first
    var swatches = card.querySelectorAll('.goessl-product-card__swatch');
    swatches.forEach(function(s, i) {
      s.classList.toggle('is-active', i === 0);
    });
    delete selectedColors[handle];
  }


  // Track selected color per product handle
  var selectedColors = {};

  // Card builder \u2014 full parity with the collection page's `goessl-product-card`:
  // badges (Neu/Sale/Edition), length badge, hover-cycle image dots, quick-view +
  // wishlist hover buttons, image-swatch row, title, price (with "Ab" prefix when
  // variants vary). Card-body click adds to canvas; quick-view, wishlist and
  // swatches keep their own delegated handlers and are excluded from the card click.
  function createMiniCard(product, collection) {
    var card = document.createElement('article');
    card.className = 'goessl-product-card outfit-cfg__product-card';
    card.dataset.handle = product.handle;

    // Hover-cycle image list (uses delegated handlers in card-product.liquid)
    var hoverImages = [];
    (product.images || []).slice(0, 6).forEach(function(im) {
      hoverImages.push(im.src.replace(/\.([^.]+)$/, '_500x.$1'));
    });
    if (hoverImages.length > 1) card.dataset.cardImages = hoverImages.join('|');

    // Badge detection
    var tagsLc = (product.tags || []).map(function(t) { return String(t).toLowerCase(); });
    var isNew = tagsLc.indexOf('neu') !== -1 || tagsLc.indexOf('2026 kollektion 1') !== -1;
    var isEdition = tagsLc.indexOf('edition') !== -1;
    // available may be null in /products/{handle}.json — treat null/undefined as available
    var isAvailable = (product.variants || []).some(function(v) { return v.available !== false; });
    var onSale = (product.variants || []).some(function(v) {
      return v.compare_at_price && parseFloat(v.compare_at_price) > parseFloat(v.price);
    });

    // \u2500\u2500 Image area \u2500\u2500
    var imgWrap = document.createElement('div');
    imgWrap.className = 'goessl-product-card__image';
    imgWrap.style.aspectRatio = '3 / 4';

    var frontImg = findFrontImage(product);
    if (frontImg) {
      var img = document.createElement('img');
      img.className = 'goessl-product-card__img';
      img.src = frontImg.src.replace(/\.([^.]+)$/, '_500x.$1');
      img.alt = product.title || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.dataset.baseSrc = img.src;
      imgWrap.appendChild(img);
    }

    // Hover-cycle dots
    if (hoverImages.length > 1) {
      var dots = document.createElement('div');
      dots.className = 'goessl-product-card__dots';
      dots.setAttribute('data-card-dots', '');
      hoverImages.forEach(function(_, i) {
        var d = document.createElement('span');
        d.className = 'goessl-product-card__dot' + (i === 0 ? ' is-active' : '');
        dots.appendChild(d);
      });
      imgWrap.appendChild(dots);
    }

    // Badges (Neu / Sale / Edition / Sold out)
    var badges = document.createElement('div');
    badges.className = 'goessl-product-card__badges';
    function addBadge(modifier, text) {
      var b = document.createElement('span');
      b.className = 'goessl-product-card__badge goessl-product-card__badge--' + modifier;
      b.textContent = text;
      badges.appendChild(b);
    }
    if (isEdition && isAvailable) addBadge('editions', 'Edition');
    if (isNew && isAvailable) addBadge('new', 'Neu');
    if (onSale) addBadge('sale', 'Sale');
    if (!isAvailable) addBadge('sold-out', 'Ausverkauft');
    if (badges.children.length) imgWrap.appendChild(badges);

    // Length badge (top-right)
    var length = getDirndlLength(product);
    if (length) {
      var lengthEl = document.createElement('span');
      lengthEl.className = 'goessl-product-card__length';
      lengthEl.textContent = length + 'cm';
      imgWrap.appendChild(lengthEl);
    }

    // Quick-view button (delegated handler in quick-view.js opens the modal)
    var qv = document.createElement('button');
    qv.type = 'button';
    qv.className = 'goessl-product-card__quick-view';
    qv.setAttribute('data-quick-view-trigger', product.handle);
    qv.setAttribute('aria-label', 'Schnellansicht');
    qv.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
    imgWrap.appendChild(qv);

    // Wishlist toggle (handled by wishlist module via delegation)
    var wl = document.createElement('button');
    wl.type = 'button';
    wl.className = 'goessl-product-card__wishlist';
    wl.setAttribute('data-wishlist-toggle', '');
    wl.setAttribute('data-product-handle', product.handle);
    wl.setAttribute('aria-label', 'Zur Merkliste hinzuf\u00FCgen');
    wl.innerHTML = '<svg class="wishlist-heart wishlist-heart--outline" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M10 17.5s-7-4.5-7-9.5a4 4 0 0 1 7-2.65A4 4 0 0 1 17 8c0 5-7 9.5-7 9.5z" stroke-linecap="round" stroke-linejoin="round"/></svg><svg class="wishlist-heart wishlist-heart--filled" width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path d="M10 17.5s-7-4.5-7-9.5a4 4 0 0 1 7-2.65A4 4 0 0 1 17 8c0 5-7 9.5-7 9.5z"/></svg>';
    imgWrap.appendChild(wl);

    card.appendChild(imgWrap);

    // \u2500\u2500 Extract unique colours \u2500\u2500
    var colors = [];
    var seenColors = {};
    (product.variants || []).forEach(function(v) {
      var farbe = v.option1 || '';
      var code = extractColorCode(farbe);
      if (!code || seenColors[code]) return;
      seenColors[code] = true;
      var name = farbe.replace(/\s\d+$/, '').trim();
      var colorImg = findImageForColor(product, code);
      // Don't surface colors that have no photo — they'd render as a solid color
      // swatch, which looks broken. Show only colors with an image; the product
      // itself stays visible as long as ≥1 colour has photos. (SJ 2026-05-22)
      if (!colorImg) return;
      colors.push({ code: code, name: name, image: colorImg, variantId: v.id });
    });

    // Image-swatch row (between image and content, like collection cards)
    if (colors.length > 1) {
      var swatchesWrap = document.createElement('div');
      swatchesWrap.className = 'goessl-product-card__swatches-wrap';

      var swatchRow = document.createElement('div');
      swatchRow.className = 'goessl-product-card__swatches';

      colors.forEach(function(color, idx) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'goessl-product-card__swatch' + (idx === 0 ? ' active' : '');
        dot.setAttribute('aria-label', 'Farbe: ' + color.name);
        dot.dataset.colorCode = color.code;
        if (color.image) {
          dot.dataset.imageSrc = color.image.src.replace(/\.([^.]+)$/, '_500x.$1');
          var swatchImg = document.createElement('img');
          swatchImg.src = color.image.src.replace(/\.([^.]+)$/, '_80x80_crop_center.$1');
          swatchImg.alt = color.name;
          swatchImg.loading = 'lazy';
          swatchImg.width = 34;
          swatchImg.height = 34;
          dot.appendChild(swatchImg);
        } else {
          dot.style.backgroundColor = getColorHex(color.code);
        }

        dot.addEventListener('click', function(e) {
          e.stopPropagation();
          swatchRow.querySelectorAll('.goessl-product-card__swatch').forEach(function(d) {
            d.classList.remove('active');
          });
          dot.classList.add('active');
          selectedColors[product.handle] = color.code;

          if (color.image) {
            var cardImg = card.querySelector('.goessl-product-card__img');
            if (cardImg) {
              cardImg.src = color.image.src.replace(/\.([^.]+)$/, '_500x.$1');
              cardImg.dataset.baseSrc = cardImg.src;
            }
            // Reset hover-cycle for new colour
            card.removeAttribute('data-card-images');
            var dotsWrap = card.querySelector('[data-card-dots]');
            if (dotsWrap) dotsWrap.style.display = 'none';
          }

          var state = getState();
          for (var i = 0; i < state.length; i++) {
            if (state[i].handle === product.handle) {
              state[i].colorCode = color.code;
              renderCanvas();
              break;
            }
          }
        });

        swatchRow.appendChild(dot);
      });

      swatchesWrap.appendChild(swatchRow);

      var arrow = document.createElement('button');
      arrow.type = 'button';
      arrow.className = 'goessl-product-card__swatches-arrow';
      arrow.setAttribute('aria-label', 'Mehr Farben');
      arrow.innerHTML = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M4.5 2.5L8 6L4.5 9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      swatchesWrap.appendChild(arrow);

      card.appendChild(swatchesWrap);
    }

    // \u2500\u2500 Content (title + price) \u2500\u2500
    var content = document.createElement('div');
    content.className = 'goessl-product-card__content';

    var title = document.createElement('h3');
    title.className = 'goessl-product-card__title';
    title.textContent = product.title || '';
    content.appendChild(title);

    if (product.variants && product.variants.length) {
      var price = document.createElement('div');
      price.className = 'goessl-product-card__price';
      var prices = product.variants.map(function(v) { return parseFloat(v.price); });
      var minP = Math.min.apply(null, prices);
      var maxP = Math.max.apply(null, prices);
      var formatted = '\u20AC' + minP.toFixed(2).replace('.', ',');
      var prefix = (maxP > minP) ? 'Ab ' : '';
      price.innerHTML = '<span class="goessl-product-card__price-regular">' + prefix + formatted + '</span>';
      content.appendChild(price);
    }

    card.appendChild(content);

    // Card-body activation adds to canvas. Skip when target is a swatch /
    // quick-view / wishlist / arrow (those have their own handlers).
    function activateCard(e) {
      if (e.target.closest('.goessl-product-card__swatch')) return false;
      if (e.target.closest('[data-quick-view-trigger]')) return false;
      if (e.target.closest('[data-wishlist-toggle]')) return false;
      if (e.target.closest('.goessl-product-card__swatches-arrow')) return false;
      var colorCode = selectedColors[product.handle] || null;
      selectProduct(product, collection, colorCode);
      highlightSelectedCards();
      return true;
    }

    // !! DO NOT REMOVE !! (iOS two-tap on :hover)
    // iOS Safari treats the first tap on an element with :hover rules as
    // "show hover state" and only fires `click` on the second tap. The
    // .goessl-product-card has multiple :hover rules (image swap, plus
    // icon, etc.) — so on iPhone it took two taps to add a product.
    // Fix: handle touchend ourselves and call preventDefault() to stop
    // the synthetic click. Track touchmove to skip scroll gestures.
    var touchMoved = false;
    card.addEventListener('touchstart', function() { touchMoved = false; }, { passive: true });
    card.addEventListener('touchmove', function() { touchMoved = true; }, { passive: true });
    card.addEventListener('touchend', function(e) {
      if (touchMoved) return; // it was a scroll, not a tap
      if (activateCard(e)) e.preventDefault(); // suppress the synthetic click
    });

    card.addEventListener('click', activateCard);

    return card;
  }

  // ═══════════════════════════════════════════
  //  CANVAS MANAGEMENT — dynamic layout, no fixed slots
  // ═══════════════════════════════════════════

  function selectProduct(product, collection, colorCode) {
    var state = getState();

    // Toggle: if already on canvas, remove it
    for (var i = 0; i < state.length; i++) {
      if (state[i].handle === product.handle) {
        saveUndo();
        state.splice(i, 1);
        renderCanvas();
        updateUI();
        applyFilters();
        return;
      }
    }

    if (state.length >= MAX_ITEMS) {
      showNotice(
        'Canvas voll',
        'Sie haben bereits ' + MAX_ITEMS + ' Stücke auf dem Canvas. Bitte entfernen Sie eines, um ein anderes hinzuzufügen.'
      );
      return;
    }
    saveUndo();

    // Pick first available variant (matching color if specified)
    var variantId = null;
    if (product.variants) {
      for (var v = 0; v < product.variants.length; v++) {
        var variant = product.variants[v];
        if (colorCode) {
          var vColor = extractColorCode(variant.option1);
          if (vColor !== colorCode) continue;
        }
        // available may be null — only skip if explicitly false
        if (variant.available !== false) { variantId = variant.id; break; }
      }
      if (!variantId && product.variants[0]) variantId = product.variants[0].id;
    }

    getState().push({
      handle: product.handle,
      collection: collection,
      variant_id: variantId,
      sizeScale: getCategorySize(collection),
      colorCode: colorCode || null
    });

    renderCanvas();
    updateUI();
    applyFilters(); // Re-filter grid for length compatibility
  }

  function removeItem(idx) {
    saveUndo();
    var state = getState();
    state.splice(idx, 1);
    renderCanvas();
    updateUI();
    applyFilters(); // Re-filter grid for length compatibility
  }

  function renderCanvas() {
    canvasItems.replaceChildren();
    var state = getState();

    if (state.length === 0) {
      if (canvasEmpty) canvasEmpty.hidden = false;
      return;
    }
    if (canvasEmpty) canvasEmpty.hidden = true;

    // Sort items: largest category size first for best grid assignment
    var sorted = state.map(function(item, idx) {
      return { item: item, origIdx: idx };
    }).sort(function(a, b) {
      return (b.item.sizeScale || 0.7) - (a.item.sizeScale || 0.7);
    });

    // Pick grid layout for this item count
    var count = Math.min(sorted.length, 8);
    var grid = GRID_LAYOUTS[count] || GRID_LAYOUTS[8];

    sorted.forEach(function(entry, gridIdx) {
      var item = entry.item;
      var product = productCache[item.handle];
      if (!product) return;

      var cell = grid[gridIdx];
      if (!cell) return;

      // Scale within cell based on category size
      var scale = item.sizeScale || 0.7;
      var cellW = cell.w * scale;
      var cellH = cell.h * scale;
      var cellX = cell.x + (cell.w - cellW) / 2;
      var cellY = cell.y + (cell.h - cellH) / 2;

      // Use custom position if dragged
      var posX = item.customX != null ? item.customX : cellX;
      var posY = item.customY != null ? item.customY : cellY;

      var el = document.createElement('div');
      el.className = 'outfit-cfg__canvas-item';
      el.style.left = (posX * 100) + '%';
      el.style.top = (posY * 100) + '%';
      // Honor custom dimensions if user previously resized
      var displayW = item.customW != null ? item.customW : cellW;
      var displayH = item.customH != null ? item.customH : cellH;
      el.style.width = (displayW * 100) + '%';
      el.style.height = (displayH * 100) + '%';
      el.style.zIndex = gridIdx + 2;
      el.style.cursor = 'grab';

      // Store cell info for drag bounds
      var slotInfo = { w: displayW, h: displayH, z: gridIdx + 2 };
      makeDraggable(el, item, slotInfo);
      makeResizable(el, item, slotInfo);

      // Use color-specific image if available
      var displayImage = null;
      if (item.colorCode) {
        displayImage = findImageForColor(product, item.colorCode);
      }
      if (!displayImage && product.images && product.images[0]) {
        displayImage = product.images[0];
      }

      if (displayImage) {
        var img = document.createElement('img');
        img.src = displayImage.src.replace(/\.([^.]+)$/, '_800x.$1');
        img.alt = product.title || '';
        el.appendChild(img);
      }

      // Tooltip with name + price
      var price = product.variants && product.variants[0] ? product.variants[0].price : null;
      var tooltip = document.createElement('div');
      tooltip.className = 'outfit-cfg__canvas-tooltip';
      tooltip.textContent = product.title + (price ? ' \u2014 \u20AC' + parseFloat(price).toFixed(2).replace('.', ',') : '');
      el.appendChild(tooltip);

      // Remove button
      var removeBtn = document.createElement('button');
      removeBtn.className = 'outfit-cfg__canvas-item__remove';
      removeBtn.textContent = '\u00D7';
      var removeIdx = entry.origIdx;
      removeBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        removeItem(removeIdx);
      });
      el.appendChild(removeBtn);

      canvasItems.appendChild(el);
    });
  }

  // ═══════════════════════════════════════════
  //  RESIZE (4 corner handles)
  // ═══════════════════════════════════════════

  function makeResizable(el, stateItem, slot) {
    var corners = ['nw', 'ne', 'sw', 'se'];
    var MIN_SIZE = 0.05; // 5% of canvas

    corners.forEach(function(corner) {
      var handle = document.createElement('div');
      handle.className = 'outfit-cfg__canvas-item__resize outfit-cfg__canvas-item__resize--' + corner;
      handle.setAttribute('aria-label', 'Größe ändern');
      el.appendChild(handle);

      var startX, startY, startLeft, startTop, startW, startH, resizing = false;
      var containerRect;

      handle.addEventListener('pointerdown', function(e) {
        e.preventDefault();
        e.stopPropagation();
        resizing = true;
        handle.setPointerCapture(e.pointerId);
        containerRect = canvasItems.getBoundingClientRect();
        startX = e.clientX;
        startY = e.clientY;
        startLeft = parseFloat(el.style.left) / 100;
        startTop = parseFloat(el.style.top) / 100;
        startW = parseFloat(el.style.width) / 100;
        startH = parseFloat(el.style.height) / 100;
        el.style.zIndex = 30;
        el.style.transition = 'none';
        el.classList.add('is-resizing');
      });

      handle.addEventListener('pointermove', function(e) {
        if (!resizing) return;
        var dx = (e.clientX - startX) / containerRect.width;
        var dy = (e.clientY - startY) / containerRect.height;
        var newLeft = startLeft, newTop = startTop, newW = startW, newH = startH;

        if (corner === 'se') {
          newW = Math.max(MIN_SIZE, Math.min(1 - newLeft, startW + dx));
          newH = Math.max(MIN_SIZE, Math.min(1 - newTop, startH + dy));
        } else if (corner === 'sw') {
          newLeft = Math.max(0, Math.min(startLeft + startW - MIN_SIZE, startLeft + dx));
          newW = startW + (startLeft - newLeft);
          newH = Math.max(MIN_SIZE, Math.min(1 - newTop, startH + dy));
        } else if (corner === 'ne') {
          newTop = Math.max(0, Math.min(startTop + startH - MIN_SIZE, startTop + dy));
          newH = startH + (startTop - newTop);
          newW = Math.max(MIN_SIZE, Math.min(1 - newLeft, startW + dx));
        } else if (corner === 'nw') {
          newLeft = Math.max(0, Math.min(startLeft + startW - MIN_SIZE, startLeft + dx));
          newW = startW + (startLeft - newLeft);
          newTop = Math.max(0, Math.min(startTop + startH - MIN_SIZE, startTop + dy));
          newH = startH + (startTop - newTop);
        }

        el.style.left = (newLeft * 100) + '%';
        el.style.top = (newTop * 100) + '%';
        el.style.width = (newW * 100) + '%';
        el.style.height = (newH * 100) + '%';
      });

      handle.addEventListener('pointerup', function(e) {
        if (!resizing) return;
        resizing = false;
        el.classList.remove('is-resizing');
        el.style.zIndex = slot.z || stateItem.priority;
        el.style.transition = '';

        // Persist custom dims + position
        stateItem.customX = parseFloat(el.style.left) / 100;
        stateItem.customY = parseFloat(el.style.top) / 100;
        stateItem.customW = parseFloat(el.style.width) / 100;
        stateItem.customH = parseFloat(el.style.height) / 100;
        // Update slot bounds for subsequent drags
        slot.w = stateItem.customW;
        slot.h = stateItem.customH;

        if (typeof updateCanvasButtons === 'function') updateCanvasButtons();
        // Refresh QR so it encodes the new layout
        updateCanvasQR();
      });
    });
  }

  // ═══════════════════════════════════════════
  //  DRAG TO REPOSITION
  // ═══════════════════════════════════════════

  function makeDraggable(el, stateItem, slot) {
    var startX, startY, startLeft, startTop, dragging = false;
    var didMove = false;
    var containerRect;

    el.addEventListener('pointerdown', function(e) {
      if (e.target.closest('.outfit-cfg__canvas-item__remove')) return;
      e.preventDefault();
      dragging = true;
      didMove = false;
      el.style.cursor = 'grabbing';
      el.style.zIndex = 20;
      el.style.transition = 'none';
      el.setPointerCapture(e.pointerId);

      containerRect = canvasItems.getBoundingClientRect();
      startX = e.clientX;
      startY = e.clientY;
      startLeft = parseFloat(el.style.left) / 100;
      startTop = parseFloat(el.style.top) / 100;
    });

    el.addEventListener('pointermove', function(e) {
      if (!dragging) return;
      var dx = (e.clientX - startX) / containerRect.width;
      var dy = (e.clientY - startY) / containerRect.height;
      if (Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01) didMove = true;
      var newX = Math.max(0, Math.min(1 - slot.w, startLeft + dx));
      var newY = Math.max(0, Math.min(1 - slot.h, startTop + dy));
      el.style.left = (newX * 100) + '%';
      el.style.top = (newY * 100) + '%';
    });

    el.addEventListener('pointerup', function(e) {
      if (!dragging) return;
      dragging = false;
      el.style.cursor = 'grab';
      el.style.zIndex = slot.z || stateItem.priority;
      el.style.transition = '';

      // Save custom position
      var finalX = parseFloat(el.style.left) / 100;
      var finalY = parseFloat(el.style.top) / 100;
      stateItem.customX = finalX;
      stateItem.customY = finalY;
      updateCanvasButtons();
      // Refresh QR so it encodes the new layout
      if (didMove) updateCanvasQR();

      if (!didMove) {
        // Tap without drag — toggle tapped state (mobile remove + tooltip)
        var wasTapped = el.classList.contains('is-tapped');
        canvasItems.querySelectorAll('.outfit-cfg__canvas-item').forEach(function(c) {
          c.classList.remove('is-tapped');
        });
        if (!wasTapped) el.classList.add('is-tapped');
      }
    });
  }

  // Drag for canvas overlays (logo, QR) — simpler, pixel-based
  function makeOverlayDraggable(el) {
    if (!el) return;
    var startX, startY, origLeft, origTop, dragging = false;
    var canvas;

    el.addEventListener('pointerdown', function(e) {
      e.preventDefault();
      dragging = true;
      el.style.cursor = 'grabbing';
      el.style.transition = 'none';
      el.setPointerCapture(e.pointerId);

      canvas = el.closest('.outfit-cfg__canvas');
      var rect = canvas.getBoundingClientRect();
      var elRect = el.getBoundingClientRect();
      startX = e.clientX;
      startY = e.clientY;
      origLeft = elRect.left - rect.left;
      origTop = elRect.top - rect.top;
      // Switch to absolute positioning
      el.style.left = origLeft + 'px';
      el.style.top = origTop + 'px';
      el.style.bottom = 'auto';
      el.style.right = 'auto';
      el.style.transform = 'none';
    });

    el.addEventListener('pointermove', function(e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      el.style.left = (origLeft + dx) + 'px';
      el.style.top = (origTop + dy) + 'px';
    });

    el.addEventListener('pointerup', function() {
      if (!dragging) return;
      dragging = false;
      el.style.cursor = 'grab';
      el.style.transition = '';
      // Convert px → normalized 0-1 of the canvas so position survives sharing.
      var canvasRect = canvas.getBoundingClientRect();
      var elRect = el.getBoundingClientRect();
      var nx = (elRect.left - canvasRect.left) / canvasRect.width;
      var ny = (elRect.top - canvasRect.top) / canvasRect.height;
      el.style.left = (nx * 100) + '%';
      el.style.top = (ny * 100) + '%';
      el.dataset.dragged = 'true';
      el.dataset.normX = String(nx);
      el.dataset.normY = String(ny);
      updateCanvasQR();
    });
  }

  // Apply a stored normalized QR position from a shared URL.
  function applyQrLayout(spec) {
    var qrEl = document.querySelector('[data-canvas-qr]');
    if (!qrEl || !spec) return;
    var nx = spec.x != null ? spec.x / 1000 : null;
    var ny = spec.y != null ? spec.y / 1000 : null;
    if (nx == null || ny == null) return;
    qrEl.style.left = (nx * 100) + '%';
    qrEl.style.top = (ny * 100) + '%';
    qrEl.style.bottom = 'auto';
    qrEl.style.right = 'auto';
    qrEl.style.transform = 'none';
    qrEl.dataset.dragged = 'true';
    qrEl.dataset.normX = String(nx);
    qrEl.dataset.normY = String(ny);
  }

  // ═══════════════════════════════════════════
  //  UNDO
  // ═══════════════════════════════════════════

  function saveUndo() {
    undoStack.push(JSON.stringify(getState()));
    if (undoStack.length > MAX_UNDO) undoStack.shift();
    updateCanvasButtons();
  }

  function undo() {
    if (!undoStack.length) return;
    var prev = JSON.parse(undoStack.pop());
    setState(prev);
    renderCanvas();
    updateUI();
    applyFilters();
    updateCanvasButtons();
  }

  function resetPositions() {
    getState().forEach(function(item) {
      delete item.customX;
      delete item.customY;
    });
    renderCanvas();
    updateCanvasButtons();
  }

  function clearCanvas() {
    saveUndo();
    setState([]);
    renderCanvas();
    updateUI();
    highlightSelectedCards();
    updateCanvasButtons();
  }

  function updateCanvasButtons() {
    var state = getState();
    var hasItems = state.length > 0;
    var hasCustom = state.some(function(item) {
      return item.customX != null || item.customY != null;
    });
    if (resetBtn) resetBtn.hidden = !hasCustom;
    if (clearBtn) clearBtn.hidden = !hasItems;
    if (undoBtn) undoBtn.hidden = !undoStack.length;
  }

  function updateUI() {
    var state = getState();
    var hasItems = state.length > 0;

    // Refresh QR code so it always points to the current outfit
    updateCanvasQR();

    // Sticky footer is replaced by the inline selected-items panel
    if (stickyFooter) stickyFooter.hidden = true;

    renderSelectedPanel();
    updateTotal();
    highlightSelectedCards();
    updateCanvasButtons();

    // Persist current outfit in the URL so a reload restores the canvas.
    // replaceState (not pushState) — we don't want every add/drag/resize
    // to add a back-button history entry.
    try {
      if (hasItems) {
        var u = getShareUrl();
        if (u && u !== window.location.href) {
          history.replaceState(null, '', u);
        }
      } else if (window.location.search) {
        history.replaceState(null, '', window.location.pathname);
      }
    } catch (e) { /* ignore — non-critical */ }
  }

  // ═══════════════════════════════════════════
  //  SELECTED ITEMS PANEL (right of canvas)
  //  Inline size + colour + remove for each item; CTA pinned to bottom.
  // ═══════════════════════════════════════════

  function renderSelectedPanel() {
    if (!selectedList) return;
    var state = getState();
    var hasItems = state.length > 0;

    if (selectedCount) selectedCount.textContent = String(state.length);

    selectedList.replaceChildren();

    if (!hasItems) {
      var empty = document.createElement('p');
      empty.className = 'outfit-cfg__selected-empty';
      empty.textContent = 'Wählen Sie Stücke aus den Kategorien unten, um Ihr Outfit zusammenzustellen.';
      selectedList.appendChild(empty);
      if (selectedCart) selectedCart.disabled = true;
      if (selectedTotal) selectedTotal.textContent = '€0,00';
      return;
    }

    state.forEach(function(item, idx) {
      var product = productCache[item.handle];
      if (!product) return;

      var row = document.createElement('div');
      row.className = 'outfit-cfg__selected-row';
      row.dataset.rowIdx = String(idx);

      // Thumbnail (uses colour-specific image when available)
      var thumb = document.createElement('div');
      thumb.className = 'outfit-cfg__selected-thumb';
      var img = null;
      if (item.colorCode) img = findImageForColor(product, item.colorCode);
      if (!img && product.images && product.images[0]) img = product.images[0];
      if (img) {
        var imgEl = document.createElement('img');
        imgEl.src = img.src.replace(/\.([^.]+)$/, '_200x.$1');
        imgEl.alt = product.title || '';
        imgEl.loading = 'lazy';
        thumb.appendChild(imgEl);
      }
      row.appendChild(thumb);

      // Info column: name, price, controls
      var info = document.createElement('div');
      info.className = 'outfit-cfg__selected-info';

      var name = document.createElement('p');
      name.className = 'outfit-cfg__selected-name';
      name.textContent = product.title || '';
      info.appendChild(name);

      if (product.variants && product.variants[0]) {
        var price = document.createElement('p');
        price.className = 'outfit-cfg__selected-price';
        price.textContent = '€' + parseFloat(product.variants[0].price).toFixed(2).replace('.', ',');
        info.appendChild(price);
      }

      var controls = document.createElement('div');
      controls.className = 'outfit-cfg__selected-controls';

      // Size dropdown (variant_id stored in state.variant_id)
      var sizeSelect = document.createElement('select');
      sizeSelect.className = 'outfit-cfg__selected-size';
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Größe wählen';
      placeholder.disabled = true;
      placeholder.selected = !item.variant_id;
      sizeSelect.appendChild(placeholder);

      // Build size list filtered by current colorCode (so only available sizes show)
      var seen = {};
      var sizeEntries = [];
      (product.variants || []).forEach(function(v) {
        // If item has a colorCode, only include matching variants
        if (item.colorCode) {
          var vCode = extractColorCode(v.option1 || '');
          if (vCode !== item.colorCode) return;
        }
        var sizeVal = v.option2 || v.option1 || v.title || '';
        var clean = sizeVal.replace(/\s*\(.*\)$/, '').trim();
        if (!clean || seen[clean]) return;
        seen[clean] = true;
        sizeEntries.push({ size: clean, variant: v });
      });

      sizeEntries.sort(function(a, b) {
        var ai = SIZE_ORDER.indexOf(a.size), bi = SIZE_ORDER.indexOf(b.size);
        if (ai === -1 && bi === -1) {
          var an = parseFloat(a.size), bn = parseFloat(b.size);
          if (!isNaN(an) && !isNaN(bn)) return an - bn;
          return a.size.localeCompare(b.size);
        }
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });

      sizeEntries.forEach(function(e) {
        var opt = document.createElement('option');
        opt.value = String(e.variant.id);
        // !! DO NOT REMOVE !! (false ausverkauft — gotcha #3)
        // Shopify /products/{handle}.json returns `available: null` for some products
        // (cache or sales-channel quirk) even when stock exists in Admin. Only mark
        // ausverkauft on an EXPLICIT false — null/undefined means "unknown", treat as
        // available. Cart-add still validates real stock at checkout.
        var isSoldOut = e.variant.available === false;
        opt.textContent = e.size + (isSoldOut ? ' — ausverkauft' : '');
        if (isSoldOut) opt.disabled = true;
        if (item.variant_id === e.variant.id) opt.selected = true;
        sizeSelect.appendChild(opt);
      });

      sizeSelect.addEventListener('change', function() {
        var newId = parseInt(sizeSelect.value, 10);
        if (!isNaN(newId)) {
          item.variant_id = newId;
          updateCartButton();
        }
      });

      controls.appendChild(sizeSelect);

      // Colour dots — only when product has more than one colour
      var colorEntries = [];
      var seenColors = {};
      (product.variants || []).forEach(function(v) {
        var code = extractColorCode(v.option1 || '');
        if (!code || seenColors[code]) return;
        seenColors[code] = true;
        var ceImg = findImageForColor(product, code);
        // Skip colors with no photo so they don't render as solid swatches in the
        // canvas colour picker. Product stays usable on its imaged colours. (SJ 2026-05-22)
        if (!ceImg) return;
        colorEntries.push({ code: code, name: (v.option1 || '').replace(/\s\d+$/, '').trim(), image: ceImg });
      });

      if (colorEntries.length > 1) {
        var colorBar = document.createElement('div');
        colorBar.className = 'outfit-cfg__selected-colors';
        colorEntries.forEach(function(c) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'outfit-cfg__selected-color';
          dot.title = c.name;
          dot.setAttribute('aria-label', 'Farbe: ' + c.name);
          var activeCode = item.colorCode || colorEntries[0].code;
          if (c.code === activeCode) dot.classList.add('is-active');
          if (c.image) {
            dot.style.backgroundImage = "url('" + c.image.src.replace(/\.([^.]+)$/, '_80x80_crop_center.$1') + "')";
            dot.style.backgroundSize = 'cover';
            dot.style.backgroundPosition = 'center';
          } else {
            dot.style.backgroundColor = getColorHex(c.code);
          }
          dot.addEventListener('click', function() {
            item.colorCode = c.code;
            // Clear stale variant_id since variants are colour-specific
            item.variant_id = null;
            renderCanvas();
            renderSelectedPanel();
            updateCartButton();
          });
          colorBar.appendChild(dot);
        });
        controls.appendChild(colorBar);
      }

      info.appendChild(controls);
      row.appendChild(info);

      // Remove button
      var removeBtn = document.createElement('button');
      removeBtn.className = 'outfit-cfg__selected-remove';
      removeBtn.type = 'button';
      removeBtn.setAttribute('aria-label', 'Entfernen');
      removeBtn.textContent = '×';
      removeBtn.addEventListener('click', function() { removeItem(idx); });
      row.appendChild(removeBtn);

      selectedList.appendChild(row);
    });

    updateCartButton();
  }

  function updateCartButton() {
    if (!selectedCart) return;
    var state = getState();
    if (!state.length) {
      selectedCart.disabled = true;
      selectedCart.textContent = 'Alle in den Warenkorb';
      return;
    }
    var missing = state.filter(function(it) { return !it.variant_id; }).length;
    selectedCart.disabled = false;
    selectedCart.textContent = missing > 0
      ? 'Größen wählen (' + missing + ')'
      : 'Alle in den Warenkorb';
  }

  function submitInlineCart() {
    if (!selectedCart) return;
    var state = getState();
    if (!state.length) return;
    var cartItems = state
      .filter(function(it) { return it.variant_id; })
      .map(function(it) {
        var item = { id: it.variant_id, quantity: 1 };
        var p = productCache[it.handle];
        var artNo = null;
        if (p && p.tags) {
          for (var ti = 0; ti < p.tags.length; ti++) {
            if (typeof p.tags[ti] === 'string' && p.tags[ti].indexOf('Artikelnr:') === 0) {
              artNo = p.tags[ti].slice(10).trim();
              break;
            }
          }
        }
        if (artNo) item.properties = { '_Artikel-Nr': String(artNo) };
        return item;
      });
    if (!cartItems.length) return;

    var origLabel = selectedCart.textContent;
    selectedCart.textContent = 'Wird hinzugefügt...';
    selectedCart.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cartItems })
    })
      .then(function(r) {
        return r.json().then(function(data) {
          if (r.ok && !data.status) return data;
          throw new Error(data.description || data.message || 'Cart error');
        });
      })
      .then(function() {
        fetch('/cart.js').then(function(r) { return r.json(); }).then(function(cart) {
          document.querySelectorAll('[data-cart-count]').forEach(function(el) {
            el.textContent = cart.item_count;
            el.hidden = !cart.item_count;
          });
        });
        document.dispatchEvent(new CustomEvent('cart:open'));
        selectedCart.textContent = origLabel;
        selectedCart.disabled = false;
      })
      .catch(function(err) {
        console.error('Inline cart add failed:', err);
        selectedCart.textContent = 'Fehler';
        setTimeout(function() {
          selectedCart.textContent = origLabel;
          selectedCart.disabled = false;
        }, 2000);
      });
  }

  function updateTotal() {
    var state = getState();
    var total = 0;
    state.forEach(function(item) {
      var product = productCache[item.handle];
      if (!product || !product.variants || !product.variants.length) return;
      var matched = null;
      if (item.variant_id) {
        for (var i = 0; i < product.variants.length; i++) {
          if (product.variants[i].id === item.variant_id) { matched = product.variants[i]; break; }
        }
      }
      if (!matched) matched = product.variants[0]; // default-variant price as preview
      total += parseFloat(matched.price);
    });
    var formatted = '\u20AC' + total.toFixed(2).replace('.', ',');
    if (totalEl) totalEl.textContent = formatted;
    if (selectedTotal) selectedTotal.textContent = formatted;
  }

  // ═══════════════════════════════════════════
  //  BOTTOM BAR
  // ═══════════════════════════════════════════

  // ═══════════════════════════════════════════
  //  GENERIC NOTICE MODAL (empty canvas, max items, etc.)
  // ═══════════════════════════════════════════

  function showNotice(title, message) {
    var modal = document.querySelector('[data-notice-modal]');
    if (!modal) {
      // Fallback if markup not present
      window.alert(message);
      return;
    }
    var titleEl = modal.querySelector('[data-notice-modal-title]');
    var msgEl = modal.querySelector('[data-notice-modal-message]');
    if (titleEl) titleEl.textContent = title || '';
    if (msgEl) msgEl.textContent = message || '';
    modal.hidden = false;
    document.body.style.overflow = 'hidden';

    var closeFn = function() {
      modal.hidden = true;
      document.body.style.overflow = '';
    };
    modal.querySelectorAll('[data-notice-modal-close]').forEach(function(el) {
      el.onclick = closeFn;
    });
  }

  function bindBottomBar() {
    if (saveBtn) {
      saveBtn.addEventListener('click', function() {
        if (!getState().length) {
          showNotice('Canvas ist leer', 'Bitte fügen Sie zuerst Stücke aus den Kategorien hinzu, bevor Sie Ihr Outfit speichern.');
          return;
        }
        if (!window.Goessl || !window.Goessl.outfits) return;

        var existing = currentOutfitId ? window.Goessl.outfits.getOutfit(currentOutfitId) : null;
        var d = new Date();
        var pad = function(n) { return n < 10 ? '0' + n : n; };
        var defaultName = 'Mein Outfit ' + pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear();
        var promptDefault = existing ? existing.name : defaultName;
        var name = prompt('Outfit-Name:', promptDefault);
        if (name === null) return;
        name = name.trim() || defaultName;

        // Capture full layout so reopening from favs reproduces exactly what
        // the user composed — positions, sizes, color, variant.
        var items = getState().map(function(item) {
          var o = {
            handle: item.handle,
            variant_id: item.variant_id,
            category_slot: item.collection,
            sizeScale: item.sizeScale != null ? item.sizeScale : null,
            colorCode: item.colorCode || null
          };
          if (item.customX != null) o.customX = item.customX;
          if (item.customY != null) o.customY = item.customY;
          if (item.customW != null) o.customW = item.customW;
          if (item.customH != null) o.customH = item.customH;
          return o;
        });

        var qrEl = document.querySelector('[data-canvas-qr]');
        var qrLayout = null;
        if (qrEl && qrEl.dataset.dragged === 'true') {
          qrLayout = {
            x: parseFloat(qrEl.dataset.normX || 0),
            y: parseFloat(qrEl.dataset.normY || 0)
          };
        }

        if (existing) {
          // Update in place — no duplicate record.
          window.Goessl.outfits.updateOutfit(currentOutfitId, {
            name: name,
            items: items,
            gender: currentGender,
            qr_layout: qrLayout
          });
        } else {
          var created = window.Goessl.outfits.createOutfit(name, items, currentGender);
          if (created && created.id) {
            currentOutfitId = created.id;
            // createOutfit's signature doesn't take qr_layout — patch it on.
            if (qrLayout) window.Goessl.outfits.updateOutfit(created.id, { qr_layout: qrLayout });
          }
        }

        saveBtn.style.color = '#fff';
        saveBtn.style.background = 'var(--goessl-green-brand, #2d5016)';
        setTimeout(function() { saveBtn.style.color = ''; saveBtn.style.background = ''; }, 1500);
      });
    }

    if (shareBtn) {
      shareBtn.addEventListener('click', function() {
        if (!getState().length) {
          showNotice('Canvas ist leer', 'Bitte fügen Sie zuerst Stücke aus den Kategorien hinzu, bevor Sie Ihr Outfit teilen.');
          return;
        }
        showSharePreview();
      });
    }

    if (cartBtn) {
      cartBtn.addEventListener('click', function() {
        if (!getState().length) {
          showNotice('Canvas ist leer', 'Bitte fügen Sie zuerst Stücke aus den Kategorien hinzu.');
          return;
        }
        openSizeModal();
      });
    }
  }

  // ═══════════════════════════════════════════
  //  SIZE SELECTION MODAL
  // ═══════════════════════════════════════════

  var sizeModal, sizeModalItems, sizeModalConfirm;
  var selectedVariants = {};

  function openSizeModal() {
    sizeModal = sizeModal || document.querySelector('[data-size-modal]');
    sizeModalItems = sizeModalItems || document.querySelector('[data-size-modal-items]');
    sizeModalConfirm = sizeModalConfirm || document.querySelector('[data-size-modal-confirm]');
    if (!sizeModal) return;

    selectedVariants = {};
    sizeModalItems.replaceChildren();

    getState().forEach(function(stateItem, stateIdx) {
      var product = productCache[stateItem.handle];
      if (!product) return;
      var itemKey = stateIdx + '_' + stateItem.handle;

      var row = document.createElement('div');
      row.className = 'outfit-cfg__size-item';

      var displayImg = null;
      if (stateItem.colorCode) {
        displayImg = findImageForColor(product, stateItem.colorCode);
      }
      if (!displayImg && product.images && product.images[0]) {
        displayImg = product.images[0];
      }
      if (displayImg) {
        var img = document.createElement('img');
        img.className = 'outfit-cfg__size-item-img';
        img.src = displayImg.src.replace(/\.([^.]+)$/, '_200x.$1');
        img.alt = product.title || '';
        row.appendChild(img);
      }

      var info = document.createElement('div');
      info.className = 'outfit-cfg__size-item-info';

      var title = document.createElement('p');
      title.className = 'outfit-cfg__size-item-title';
      title.textContent = product.title;
      info.appendChild(title);

      if (product.variants && product.variants[0]) {
        var price = document.createElement('p');
        price.className = 'outfit-cfg__size-item-price';
        price.textContent = '\u20AC' + parseFloat(product.variants[0].price).toFixed(2).replace('.', ',');
        info.appendChild(price);
      }

      var options = document.createElement('div');
      options.className = 'outfit-cfg__size-item-options';

      // Collect unique sizes with their variant info, then sort
      var sizeEntries = [];
      var seenSizes = {};
      (product.variants || []).forEach(function(v) {
        var sizeVal = v.option2 || v.option1 || v.title;
        var cleanSize = sizeVal.replace(/\s*\(.*\)$/, '').trim();
        if (seenSizes[cleanSize]) return;
        seenSizes[cleanSize] = true;
        sizeEntries.push({ size: cleanSize, variant: v });
      });

      sizeEntries.sort(function(a, b) {
        var aIdx = SIZE_ORDER.indexOf(a.size);
        var bIdx = SIZE_ORDER.indexOf(b.size);
        if (aIdx === -1 && bIdx === -1) {
          var aNum = parseFloat(a.size), bNum = parseFloat(b.size);
          if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
          return a.size.localeCompare(b.size);
        }
        if (aIdx === -1) return 1;
        if (bIdx === -1) return -1;
        return aIdx - bIdx;
      });

      sizeEntries.forEach(function(entry) {
        var v = entry.variant;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'outfit-cfg__size-option';
        // available may be null — only mark unavailable if EXPLICITLY false
        if (v.available === false) btn.className += ' is-unavailable';
        btn.textContent = entry.size;

        btn.addEventListener('click', function() {
          if (v.available === false) return;
          options.querySelectorAll('.outfit-cfg__size-option').forEach(function(b) { b.classList.remove('is-selected'); });
          btn.classList.add('is-selected');
          selectedVariants[itemKey] = v.id;
          stateItem.variant_id = v.id;
          checkAllSizesSelected();
        });

        options.appendChild(btn);
      });

      info.appendChild(options);
      row.appendChild(info);
      sizeModalItems.appendChild(row);

      if (stateItem.variant_id) selectedVariants[itemKey] = stateItem.variant_id;
    });

    sizeModalConfirm.onclick = confirmSizeAndCart;
    document.querySelectorAll('[data-size-modal-close]').forEach(function(el) { el.onclick = closeSizeModal; });

    checkAllSizesSelected();
    sizeModal.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  function closeSizeModal() {
    if (sizeModal) sizeModal.hidden = true;
    document.body.style.overflow = '';
  }

  function checkAllSizesSelected() {
    var allSelected = getState().every(function(item, idx) {
      return selectedVariants[idx + '_' + item.handle];
    });
    if (sizeModalConfirm) sizeModalConfirm.disabled = !allSelected;
  }

  function confirmSizeAndCart() {
    var cartItems = [];
    getState().forEach(function(item, idx) {
      var vid = selectedVariants[idx + '_' + item.handle];
      if (!vid) return;
      var lineItem = { id: vid, quantity: 1 };
      var p = productCache[item.handle];
      var artNo = null;
        if (p && p.tags) {
          for (var ti = 0; ti < p.tags.length; ti++) {
            if (typeof p.tags[ti] === 'string' && p.tags[ti].indexOf('Artikelnr:') === 0) {
              artNo = p.tags[ti].slice(10).trim();
              break;
            }
          }
        }
      if (artNo) lineItem.properties = { '_Artikel-Nr': String(artNo) };
      cartItems.push(lineItem);
    });
    if (!cartItems.length) return;

    sizeModalConfirm.textContent = 'Wird hinzugef\u00FCgt...';
    sizeModalConfirm.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cartItems })
    })
    .then(function(r) {
      return r.json().then(function(data) {
        if (r.ok && !data.status) return data;
        throw new Error(data.description || data.message || 'Cart error');
      });
    })
    .then(function() {
      closeSizeModal();
      // Update cart count in header
      fetch('/cart.js').then(function(r) { return r.json(); }).then(function(cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function(el) {
          el.textContent = cart.item_count;
          el.hidden = !cart.item_count;
        });
      });
      document.dispatchEvent(new CustomEvent('cart:open'));
    })
    .catch(function(err) {
      console.error('Outfit cart add failed:', err);
      sizeModalConfirm.textContent = 'Fehler';
      sizeModalConfirm.disabled = false;
      setTimeout(function() { sizeModalConfirm.textContent = 'In den Warenkorb'; sizeModalConfirm.disabled = false; }, 2000);
    });
  }

  // ═══════════════════════════════════════════
  //  SHARE
  // ═══════════════════════════════════════════

  // Compute the effective layout for the current state: same algorithm
  // renderCanvas uses, returning a map keyed by the item's original index in
  // state[]. This lets getShareUrl serialize the EXACT positions a viewer
  // would see, regardless of viewport. Without this, a desktop user sharing
  // an unmodified outfit would have empty position data in the URL, and the
  // mobile recipient's auto-layout might choose different positions than the
  // desktop layout if the canvas aspect ratio ever drifted (it's currently
  // 1:1 on both, but better safe than have a discrepancy bug later).
  function getEffectivePositions() {
    var state = getState();
    var sorted = state.map(function(item, idx) {
      return { item: item, origIdx: idx };
    }).sort(function(a, b) {
      return (b.item.sizeScale || 0.7) - (a.item.sizeScale || 0.7);
    });
    var count = Math.min(sorted.length, 8);
    var grid = GRID_LAYOUTS[count] || GRID_LAYOUTS[8];
    var effective = {};
    sorted.forEach(function(entry, gridIdx) {
      var cell = grid[gridIdx];
      if (!cell) return;
      var scale = entry.item.sizeScale || 0.7;
      var cellW = cell.w * scale;
      var cellH = cell.h * scale;
      effective[entry.origIdx] = {
        x: cell.x + (cell.w - cellW) / 2,
        y: cell.y + (cell.h - cellH) / 2,
        w: cellW,
        h: cellH
      };
    });
    return effective;
  }

  function getShareUrl() {
    var state = getState();
    var origin = window.location.origin + window.location.pathname;

    var qrEl = document.querySelector('[data-canvas-qr]');
    var qrMoved = qrEl && qrEl.dataset.dragged === 'true';
    var effective = getEffectivePositions();

    // Always use the rich form so positions, sizes, colors, and variants
    // are explicit. Receiver renders the exact layout the sender saw,
    // regardless of viewport. URL gets shortened by mintShortUrl for the
    // QR code, so length isn't a UX concern.
    var payload = {
      g: currentGender[0],
      i: state.map(function(item, idx) {
        var eff = effective[idx] || {};
        var o = { h: item.handle };
        if (item.collection) o.c = item.collection;
        if (item.variant_id) o.v = item.variant_id;
        if (item.sizeScale != null) o.s = Math.round(item.sizeScale * 1000);
        if (item.colorCode) o.cc = item.colorCode;
        // Always include explicit positions/sizes — use custom if set, else
        // computed effective from the grid algorithm.
        var posX = item.customX != null ? item.customX : eff.x;
        var posY = item.customY != null ? item.customY : eff.y;
        var sizeW = item.customW != null ? item.customW : eff.w;
        var sizeH = item.customH != null ? item.customH : eff.h;
        if (posX != null) o.x = Math.round(posX * 1000);
        if (posY != null) o.y = Math.round(posY * 1000);
        if (sizeW != null) o.w = Math.round(sizeW * 1000);
        if (sizeH != null) o.H = Math.round(sizeH * 1000);
        return o;
      })
    };
    if (qrMoved) {
      payload.q = {
        x: Math.round(parseFloat(qrEl.dataset.normX || 0) * 1000),
        y: Math.round(parseFloat(qrEl.dataset.normY || 0) * 1000)
      };
    }
    var encoded = btoa(JSON.stringify(payload))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return origin + '?l=' + encoded;
  }

  // QR code generation: lazy-load qrcode-generator from CDN, render onto a canvas.
  // Called whenever the outfit changes so the QR always encodes the current share URL.
  var qrLibLoaded = false;
  function loadQrLib() {
    if (qrLibLoaded || window.qrcode) { qrLibLoaded = true; return Promise.resolve(); }
    return new Promise(function(resolve, reject) {
      var s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js';
      s.async = true;
      s.onload = function() { qrLibLoaded = true; resolve(); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function updateCanvasQR() {
    var qrEl = document.querySelector('[data-canvas-qr]');
    if (!qrEl) return;
    var longUrl = getShareUrl();
    // Tag the QR element with the URL we're about to render so we can
    // detect stale callbacks if the outfit changes mid-mint.
    qrEl.dataset.pendingUrl = longUrl;
    // Mint a short URL so the QR encodes ~50 chars instead of 300+.
    // mintShortUrl is cached by long-URL signature, so this is cheap on re-render.
    mintShortUrl(longUrl).then(function(url) {
      if (qrEl.dataset.pendingUrl !== longUrl) return; // stale — newer state in flight
      renderQrSvg(qrEl, url);
    });
  }

  function renderQrSvg(qrEl, url) {
    loadQrLib().then(function() {
      // Level M tolerates ~15% damage — sufficient now that the brand mark
      // sits standalone above the canvas, not inside the QR.
      var qr = window.qrcode(0, 'M');
      qr.addData(url);
      qr.make();
      var modules = qr.getModuleCount();
      var SVG_NS = 'http://www.w3.org/2000/svg';
      var size = modules; // unitless; SVG viewBox handles scaling
      // Build an SVG (vector — sharp at any rendered size, no pixelation)
      var existing = qrEl.querySelector('svg.outfit-cfg__qr-svg');
      if (existing) existing.remove();
      var svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('class', 'outfit-cfg__qr-svg');
      svg.setAttribute('viewBox', '0 0 ' + size + ' ' + size);
      svg.setAttribute('xmlns', SVG_NS);
      svg.setAttribute('aria-label', 'Outfit QR Code');
      svg.setAttribute('shape-rendering', 'crispEdges');
      // White background
      var bg = document.createElementNS(SVG_NS, 'rect');
      bg.setAttribute('x', 0); bg.setAttribute('y', 0);
      bg.setAttribute('width', size); bg.setAttribute('height', size);
      bg.setAttribute('fill', '#ffffff');
      svg.appendChild(bg);
      // Dark modules
      for (var r = 0; r < modules; r++) {
        for (var c = 0; c < modules; c++) {
          if (qr.isDark(r, c)) {
            var rect = document.createElementNS(SVG_NS, 'rect');
            rect.setAttribute('x', c);
            rect.setAttribute('y', r);
            rect.setAttribute('width', 1);
            rect.setAttribute('height', 1);
            rect.setAttribute('fill', '#00561C');
            svg.appendChild(rect);
          }
        }
      }
      // Insert BEFORE the logo so logo overlays on top
      qrEl.insertBefore(svg, qrEl.firstChild);
    }).catch(function() {
      // Library load failed — clear SVG only, keep the logo overlay
      var existing = qrEl.querySelector('svg.outfit-cfg__qr-svg');
      if (existing) existing.remove();
    });
  }

  function loadFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var oid = params.get('oid');
    var rich = params.get('l');
    var compact = params.get('o');
    var legacy = params.get('outfit');
    var addHandle = params.get('add'); // PDP → configurator handoff: ?add={handle}

    // Single-handle add from a PDP "Zum Konfigurator" button
    if (addHandle && !oid && !rich && !compact && !legacy) {
      loadHandlesOntoCanvas(addHandle.split(',').filter(Boolean));
      return;
    }

    // Reopen a saved outfit from localStorage with full layout.
    // !! DO NOT REMOVE !! (wishlist.js race)
    // Defer-script execution order between layout-included wishlist.js and
    // section-included outfit-configurator.js is not guaranteed across all
    // browsers (confirmed by SJ console: "window.Goessl.outfits not available").
    // If oid is present but the outfits API hasn't booted yet, poll briefly
    // (max 2s) and retry. After that, give up and fall through to other
    // params so the page doesn't hang.
    if (oid && (!window.Goessl || !window.Goessl.outfits)) {
      var tries = 0;
      var maxTries = 40; // 40 × 50ms = 2s
      var retryTimer = setInterval(function() {
        tries++;
        if (window.Goessl && window.Goessl.outfits) {
          clearInterval(retryTimer);
          loadFromUrl(); // re-enter with API now available
        } else if (tries >= maxTries) {
          clearInterval(retryTimer);
          console.warn('[configurator] window.Goessl.outfits never became available — saved outfit cannot be loaded.');
        }
      }, 50);
      return;
    }

    if (oid) console.log('[configurator] Loading saved outfit by oid:', oid);
    if (oid && window.Goessl && window.Goessl.outfits) {
      var saved = window.Goessl.outfits.getOutfit(oid);
      console.log('[configurator] getOutfit result:', saved);
      if (saved) {
        currentOutfitId = oid;
        currentGender = saved.gender || 'frauen';
        setGenderUI();
        var specs = (saved.items || [])
          .filter(function(it) { return it && it.handle; }) // !! drop items without handle (PDP outfits sometimes saved with empty handle)
          .map(function(it) {
            var o = { h: it.handle };
            if (it.category_slot || it.collection) o.c = it.category_slot || it.collection;
            if (it.variant_id) o.v = it.variant_id;
            if (it.sizeScale != null) o.s = Math.round(it.sizeScale * 1000);
            if (it.colorCode) o.cc = it.colorCode;
            if (it.customX != null) o.x = Math.round(it.customX * 1000);
            if (it.customY != null) o.y = Math.round(it.customY * 1000);
            if (it.customW != null) o.w = Math.round(it.customW * 1000);
            if (it.customH != null) o.H = Math.round(it.customH * 1000);
            return o;
          });
        console.log('[configurator] Loading', specs.length, 'items from saved outfit. specs:', specs);
        if (!specs.length) {
          console.warn('[configurator] Outfit has no items with valid handles. Saved data:', saved);
        }
        var qrSpec = saved.qr_layout ? {
          x: Math.round((saved.qr_layout.x || 0) * 1000),
          y: Math.round((saved.qr_layout.y || 0) * 1000)
        } : null;
        loadHandlesOntoCanvasWithLayout(specs, qrSpec);
        return;
      } else {
        console.warn('[configurator] No saved outfit found with id:', oid, 'Available outfits:',
          window.Goessl.outfits.getOutfits ? window.Goessl.outfits.getOutfits().map(function(o){return o.id}) : '(getOutfits not exposed)');
      }
    }

    if (rich) {
      try {
        var b64 = rich.replace(/-/g, '+').replace(/_/g, '/');
        while (b64.length % 4) b64 += '=';
        var payload = JSON.parse(atob(b64));
        currentGender = payload.g === 'm' ? 'maenner' : 'frauen';
        setGenderUI();
        loadHandlesOntoCanvasWithLayout(payload.i || [], payload.q || null);
        return;
      } catch (e) {}
    }

    if (compact) {
      var parts = compact.split(':');
      if (parts.length >= 2) {
        currentGender = parts[0] === 'm' ? 'maenner' : 'frauen';
        setGenderUI();
        loadHandlesOntoCanvas(parts[1].split(',').filter(Boolean));
      }
    } else if (legacy) {
      try {
        var decoded = JSON.parse(atob(legacy));
        if (decoded.gender) { currentGender = decoded.gender; setGenderUI(); }
        if (decoded.items) loadHandlesOntoCanvas(decoded.items.map(function(i) { return i.handle; }));
      } catch (e) {}
    }
  }

  // Like loadHandlesOntoCanvas but applies positions/sizes/color/variant from URL spec.
  function loadHandlesOntoCanvasWithLayout(specs, qrSpec) {
    var fetches = specs.map(function(spec) {
      return fetch('/products/' + encodeURIComponent(spec.h) + '.json')
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(data) {
          if (!data || !data.product) return null;
          productCache[data.product.handle] = data.product;
          return { product: data.product, spec: spec };
        }).catch(function() { return null; });
    });

    Promise.all(fetches).then(function(results) {
      results.forEach(function(r) {
        if (!r) return;
        var product = r.product, spec = r.spec;
        var collection = spec.c || guessCollectionForProduct(product);
        var variantId = spec.v ||
          (product.variants && product.variants[0] ? product.variants[0].id : null);
        var item = {
          handle: product.handle,
          collection: collection,
          variant_id: variantId,
          sizeScale: spec.s != null ? spec.s / 1000 : getCategorySize(collection),
          colorCode: spec.cc || null
        };
        if (spec.x != null) item.customX = spec.x / 1000;
        if (spec.y != null) item.customY = spec.y / 1000;
        if (spec.w != null) item.customW = spec.w / 1000;
        if (spec.H != null) item.customH = spec.H / 1000;
        getState().push(item);
      });
      renderCanvas();
      updateUI();
      if (qrSpec) applyQrLayout(qrSpec);
    });
  }

  function setGenderUI() {
    // Gender toggle UI removed — currentGender is derived from category selection.
    // When loading a saved/shared outfit with a gender hint, jump to a sensible
    // default category for that gender so the dropdown isn't stale.
    var select = document.querySelector('.outfit-cfg__cat-select');
    if (!select) return;
    var defaults = { frauen: 'frauen-dirndl', maenner: 'maenner-lederhosen', kinder: 'kinder-dirndl' };
    var target = defaults[currentGender] || 'frauen-dirndl';
    var has = false;
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value === target) { has = true; break; }
    }
    if (has) {
      select.value = target;
      showProductGrid(target);
    }
  }

  function loadHandlesOntoCanvas(handles) {
    var fetches = handles.map(function(spec) {
      // spec may be "handle" or "handle:colorCode" (PDP / newsletter handoff)
      var sep = String(spec).indexOf(':');
      var handle = sep === -1 ? String(spec) : String(spec).slice(0, sep);
      var wantColor = sep === -1 ? null : String(spec).slice(sep + 1);
      return fetch('/products/' + encodeURIComponent(handle) + '.json')
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(data) {
          if (!data || !data.product) return null;
          productCache[data.product.handle] = data.product;
          return { product: data.product, wantColor: wantColor };
        }).catch(function() { return null; });
    });

    Promise.all(fetches).then(function(results) {
      results.forEach(function(res) {
        if (!res || !res.product) return;
        var product = res.product;
        var variants = product.variants || [];
        // Pick the variant for the requested colour; otherwise the first variant.
        var variant = variants[0] || null;
        if (res.wantColor) {
          for (var k = 0; k < variants.length; k++) {
            if (extractColorCode(variants[k].option1) === res.wantColor) { variant = variants[k]; break; }
          }
        }
        // Always derive colorCode from the chosen variant so the canvas shows the
        // matching colour image (renderCanvas keys off colorCode, not variant_id).
        var colorCode = variant ? extractColorCode(variant.option1) : null;
        var guessedCollection = guessCollectionForProduct(product);

        getState().push({
          handle: product.handle,
          collection: guessedCollection,
          variant_id: variant ? variant.id : null,
          colorCode: colorCode,
          sizeScale: getCategorySize(guessedCollection)
        });
      });
      renderCanvas();
      updateUI();
    });
  }

  function guessCollectionForProduct(product) {
    // Try to guess collection from product type or tags.
    // Shopify's /products/{handle}.json returns tags as comma-separated STRING,
    // while the Storefront/Admin API returns an array. Handle both shapes.
    var ptype = (product.product_type || '').toLowerCase();
    var rawTags = product.tags;
    var tags = Array.isArray(rawTags) ? rawTags.join(' ') : (rawTags || '').toString();
    tags = tags.toLowerCase();
    var combined = ptype + ' ' + tags;
    var cats = Object.keys(CATEGORY_SIZES);
    for (var i = 0; i < cats.length; i++) {
      if (combined.indexOf(cats[i]) !== -1) return currentGender + '-' + cats[i];
    }
    return currentGender + '-accessoires';
  }

  // ═══════════════════════════════════════════
  //  SCREENSHOT + R2 UPLOAD
  // ═══════════════════════════════════════════

  function showSharePreview() {
    var canvas = document.querySelector('[data-outfit-canvas]');
    if (!canvas) return;

    // !! EMERGENCY BYPASS — iOS Safari html2canvas keeps hanging.
    // On any iOS device, skip the screenshot entirely and open the share
    // popup immediately with a 1x1 placeholder. Facebook/Pinterest/
    // Instagram/Link kopieren all work off the share URL. Bild speichern
    // produces a 1x1 PNG (acceptable trade-off until server-side
    // renderer ships).
    var isIOS = /iP(hone|ad|od)/i.test(navigator.userAgent) ||
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isIOS) {
      var placeholder = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkAAIAAAoAAv/lxKUAAAAASUVORK5CYII=';
      try { openSharePopup(placeholder); } catch (e) { console.warn('share popup error', e); }
      return;
    }

    shareBtn.style.opacity = '0.5';
    shareBtn.style.pointerEvents = 'none';

    var actionsEl = canvas.querySelector('.outfit-cfg__canvas-actions');
    if (actionsEl) actionsEl.style.display = 'none';
    canvas.classList.add('is-capturing'); // disables mix-blend-mode on item images

    // Force opacity:1 on each canvas-item AND its inner <img> directly via
    // inline style. iOS Safari + html2canvas-pro can otherwise read a
    // mid-animation opacity value, producing a faded screenshot. Inline
    // style outranks the keyframe so both stay visually solid.
    var capturedItems = canvas.querySelectorAll('.outfit-cfg__canvas-item');
    var savedStyles = [];
    Array.prototype.forEach.call(capturedItems, function(el) {
      savedStyles.push({ el: el, opacity: el.style.opacity });
      el.style.opacity = '1';
      var img = el.querySelector('img');
      if (img) {
        savedStyles.push({ el: img, opacity: img.style.opacity });
        img.style.opacity = '1';
      }
    });

    function cleanup() {
      shareBtn.style.opacity = '';
      shareBtn.style.pointerEvents = '';
      if (actionsEl) actionsEl.style.display = '';
      canvas.classList.remove('is-capturing');
      savedStyles.forEach(function(s) { s.el.style.opacity = s.opacity; });
    }

    // Wait for every <img> inside the canvas to load before capture.
    var canvasImages = canvas.querySelectorAll('img');
    var imagesReady = Promise.all(Array.prototype.map.call(canvasImages, function(img) {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise(function(resolve) {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    }));

    // Watchdog — recover after 10s if anything stalls.
    var stalled = false;
    var watchdog = setTimeout(function() {
      stalled = true;
      cleanup();
      console.warn('[configurator] Share capture stalled — recovered.');
      if (typeof showNotice === 'function') {
        try { showNotice('Teilen nicht möglich', 'Bitte versuchen Sie es erneut.'); }
        catch (e) {}
      }
    }, 10000);

    imagesReady
      .then(loadHtml2Canvas)
      .then(function() { return new Promise(function(r) { setTimeout(r, 250); }); })
      .then(function() {
        if (stalled) throw new Error('aborted');
        // Simplest possible html2canvas call. Past attempts to override
        // width/height/x/y/windowWidth caused mis-sized/cropped captures
        // (SJ confirmed: capture rendered as a tiny top-left tile with
        // most of the area as empty cream). Let html2canvas measure the
        // element itself.
        return html2canvas(canvas, {
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#f0ede8',
          scale: Math.min(window.devicePixelRatio || 1, 2)
          // foreignObjectRendering: true was tried — produces a blank PNG
          // on iOS Safari due to cross-origin image security restrictions
          // in SVG <foreignObject>. Stick with the default raster path; the
          // mix-blend-mode + opacity workarounds (is-capturing class +
          // inline opacity:1) handle the fade.
        });
      })
      .then(function(rendered) {
        if (stalled) return;
        clearTimeout(watchdog);
        cleanup();
        openSharePopup(rendered.toDataURL('image/png'));
      })
      .catch(function(err) {
        clearTimeout(watchdog);
        cleanup();
        console.error('Share capture failed:', err);
      });
  }

  // SVG icons for the share destinations (inline so the popup is self-contained).
  var SHARE_ICONS = {
    facebook: '<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.91 3.78-3.91 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.44 2.91h-2.34V22c4.78-.76 8.43-4.92 8.43-9.94Z"/></svg>',
    pinterest: '<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12.04 2C6.5 2 2 6.5 2 12.04c0 4.25 2.64 7.88 6.37 9.34-.09-.79-.17-2.02.04-2.89.18-.78 1.16-4.97 1.16-4.97s-.3-.6-.3-1.48c0-1.39.81-2.43 1.81-2.43.85 0 1.27.64 1.27 1.4 0 .86-.55 2.14-.83 3.32-.23.99.5 1.8 1.48 1.8 1.78 0 3.14-1.88 3.14-4.59 0-2.4-1.72-4.07-4.18-4.07-2.85 0-4.52 2.13-4.52 4.34 0 .86.33 1.78.74 2.28.08.1.09.18.07.28-.07.31-.24.99-.27 1.13-.04.18-.14.22-.32.13-1.21-.56-1.96-2.32-1.96-3.74 0-3.04 2.21-5.84 6.37-5.84 3.34 0 5.94 2.38 5.94 5.57 0 3.32-2.1 6-5.01 6-.98 0-1.9-.51-2.21-1.11l-.6 2.29c-.22.84-.81 1.89-1.21 2.53.91.28 1.87.43 2.86.43 5.54 0 10.04-4.5 10.04-10.04C22.08 6.5 17.58 2 12.04 2Z"/></svg>',
    instagram: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor"/></svg>',
    download: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M12 4v12"/><path d="M7 11l5 5 5-5"/><path d="M5 21h14"/></svg>',
    link: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1"/><path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1"/></svg>'
  };

  // Build the social-button popup. Uploads to R2 once, mints short URL once,
  // then offers per-destination buttons that reuse those values.
  function openSharePopup(dataUrl) {
    // dataUrl might be a 1×1 placeholder (iOS popup-open bypass) or a real
    // screenshot (desktop). Anything that consumes the image is now lazy:
    // first button click triggers html2canvas if needed, result is cached.
    var IS_PLACEHOLDER = dataUrl && dataUrl.length < 200;

    // Lazy real-screenshot — runs html2canvas on iOS only on first call.
    var realDataUrlPromise = null;
    function getRealDataUrl() {
      if (realDataUrlPromise) return realDataUrlPromise;
      if (!IS_PLACEHOLDER) {
        realDataUrlPromise = Promise.resolve(dataUrl);
        return realDataUrlPromise;
      }
      realDataUrlPromise = captureCanvasOnDemand();
      return realDataUrlPromise;
    }

    // Lazy file (for navigator.share — Instagram + iMessage share sheet).
    var filePromise = null;
    function getFile() {
      if (filePromise) return filePromise;
      filePromise = getRealDataUrl().then(function(du) {
        var byteString = atob(du.split(',')[1]);
        var ab = new ArrayBuffer(byteString.length);
        var ia = new Uint8Array(ab);
        for (var i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
        var blob = new Blob([ab], { type: 'image/png' });
        return new File([blob], 'goessl-outfit.png', { type: 'image/png' });
      });
      return filePromise;
    }

    // Lazy R2 upload — Pinterest needs the public image URL.
    var r2UrlPromise = null;
    function getR2Url() {
      if (r2UrlPromise) return r2UrlPromise;
      r2UrlPromise = getRealDataUrl().then(function(du) {
        return fetch(R2_UPLOAD_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image: du })
        }).then(function(r) { return r.json(); }).then(function(data) {
          if (data && data.url) return data.url;
          throw new Error((data && data.error) || 'r2_upload_failed');
        });
      });
      return r2UrlPromise;
    }

    // Three URLs at play:
    //   • shortUrl (gsl.asksj.com/s/...) → QR code only (density)
    //   • humanShareUrl (goessl.com/...?l=...) → Copy-link button (clean
    //     branded URL the user pastes anywhere)
    //   • ogUrl (gsl.asksj.com/o/...) → Facebook + Pinterest. When their
    //     crawlers hit it, the Worker serves HTML with og:image pointing at
    //     the outfit screenshot, so link previews actually show the outfit.
    //     Humans clicking the same link get 302'd to goessl.com.
    var humanShareUrl = getShareUrl();
    var shortUrlPromise = mintShortUrl(humanShareUrl);

    // Lazy ogUrl — depends on r2 upload completing first.
    var ogUrlPromise = null;
    function getOgUrl() {
      if (ogUrlPromise) return ogUrlPromise;
      ogUrlPromise = getR2Url().then(function(imageUrl) {
        return mintOgUrl(humanShareUrl, imageUrl);
      });
      return ogUrlPromise;
    }

    // On-demand capture using NATIVE Canvas API — bypasses html2canvas
    // entirely so it works reliably on iOS Safari. PRE-LOADS each product
    // image via fetch + Blob → Object URL so the canvas isn't tainted by
    // cross-origin Shopify CDN images (which would block toDataURL).
    function captureCanvasOnDemand() {
      var canvasEl = document.querySelector('[data-outfit-canvas]');
      if (!canvasEl) return Promise.reject(new Error('no canvas'));
      var rect = canvasEl.getBoundingClientRect();
      var size = Math.round(Math.min(rect.width, rect.height) || 600);
      var dpr = Math.min(window.devicePixelRatio || 1, 2);

      // Helper: load image with crossOrigin=anonymous so drawImage() doesn't
      // taint the canvas. Shopify CDN sets Access-Control-Allow-Origin: *
      // so this just works. Falls back to the live <img> reference if the
      // CORS load fails — drawImage will succeed but toDataURL will throw,
      // caught at the toDataURL site.
      function loadCorsImage(src, fallbackImg) {
        return new Promise(function(resolve) {
          var im = new Image();
          im.crossOrigin = 'anonymous';
          im.onload = function() { resolve(im); };
          im.onerror = function() {
            console.warn('CORS image load failed, falling back:', src);
            resolve(fallbackImg || null);
          };
          im.src = src;
        });
      }

      function svgToImage(svgEl, forcedFill) {
        // Clone so we don't mutate the live DOM element. The serialized SVG
        // is rendered standalone (no parent stylesheet), so any CSS-driven
        // fill colour falls back to black. Force the fill via inline
        // attributes on all child elements + the root.
        var clone = svgEl.cloneNode(true);
        if (forcedFill) {
          clone.setAttribute('fill', forcedFill);
          var nodes = clone.querySelectorAll('*');
          for (var i = 0; i < nodes.length; i++) {
            // Only override if the node doesn't already have an explicit
            // non-black fill (preserves multi-colour SVGs like QR codes).
            var existing = nodes[i].getAttribute('fill');
            if (!existing || existing === 'currentColor' || existing === '') {
              nodes[i].setAttribute('fill', forcedFill);
            }
          }
        }
        var svgStr = new XMLSerializer().serializeToString(clone);
        var blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
        var url = URL.createObjectURL(blob);
        return new Promise(function(resolve, reject) {
          var im = new Image();
          im.onload = function() { resolve(im); };
          im.onerror = function() { URL.revokeObjectURL(url); reject(new Error('svg load failed')); };
          im.src = url;
        });
      }

      // Collect items and pre-fetch product images via CORS so canvas
      // doesn't get tainted.
      var items = Array.prototype.slice.call(canvasEl.querySelectorAll('.outfit-cfg__canvas-item'));
      var itemPlans = items.map(function(item) {
        var img = item.querySelector('img');
        if (!img || !img.naturalWidth || !img.src) return null;
        var iR = item.getBoundingClientRect();
        var ix = iR.left - rect.left;
        var iy = iR.top - rect.top;
        var iw = iR.width;
        var ih = iR.height;
        return {
          src: img.src,
          aspect: img.naturalWidth / img.naturalHeight,
          ix: ix, iy: iy, iw: iw, ih: ih
        };
      }).filter(Boolean);

      var brandEl = canvasEl.querySelector('.outfit-cfg__canvas-brand');
      var qrEl = canvasEl.querySelector('.outfit-cfg__canvas-qr');

      return Promise.all(itemPlans.map(function(plan) {
        return loadCorsImage(plan.src).then(function(im) {
          plan.image = im;
          return plan;
        });
      })).then(function(loadedPlans) {
        var c = document.createElement('canvas');
        c.width = size * dpr;
        c.height = size * dpr;
        var ctx = c.getContext('2d');
        ctx.scale(dpr, dpr);

        // Background
        ctx.fillStyle = '#f5f3ef';
        ctx.fillRect(0, 0, size, size);

        // Products
        loadedPlans.forEach(function(plan) {
          if (!plan.image) return;
          var dw, dh;
          if (plan.iw / plan.ih > plan.aspect) { dh = plan.ih; dw = plan.ih * plan.aspect; }
          else { dw = plan.iw; dh = plan.iw / plan.aspect; }
          var dx = plan.ix + (plan.iw - dw) / 2;
          var dy = plan.iy + (plan.ih - dh) / 2;
          ctx.save();
          ctx.globalCompositeOperation = 'multiply';
          try { ctx.drawImage(plan.image, dx, dy, dw, dh); }
          catch (e) { console.warn('drawImage failed:', e); }
          ctx.restore();
        });

        // Border
        ctx.strokeStyle = '#00561C';
        ctx.lineWidth = 2;
        ctx.strokeRect(13, 13, size - 26, size - 26);
        ctx.strokeStyle = 'rgba(0, 86, 28, 0.5)';
        ctx.lineWidth = 1;
        ctx.strokeRect(17, 17, size - 34, size - 34);

        // Brand + QR (SVG → Image)
        var asyncDraws = [];
        if (brandEl) {
          var brandSvg = brandEl.querySelector('svg');
          if (brandSvg) {
            var bR = brandEl.getBoundingClientRect();
            // Force Gössl green — the live SVG fills via CSS which doesn't
            // apply to a serialized standalone SVG.
            asyncDraws.push(svgToImage(brandSvg, '#00561C').then(function(im) {
              ctx.drawImage(im, bR.left - rect.left, bR.top - rect.top, bR.width, bR.height);
            }).catch(function() { /* skip */ }));
          }
        }
        if (qrEl) {
          var qR = qrEl.getBoundingClientRect();
          var qx = qR.left - rect.left, qy = qR.top - rect.top;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(qx, qy, qR.width, qR.height);
          var qrSvg = qrEl.querySelector('svg');
          var qrCanvas = qrEl.querySelector('canvas');
          if (qrSvg) {
            asyncDraws.push(svgToImage(qrSvg).then(function(im) {
              var pad = 8;
              ctx.drawImage(im, qx + pad, qy + pad, qR.width - pad * 2, qR.height - pad * 2);
            }).catch(function() { /* skip */ }));
          } else if (qrCanvas) {
            var pad2 = 8;
            try { ctx.drawImage(qrCanvas, qx + pad2, qy + pad2, qR.width - pad2 * 2, qR.height - pad2 * 2); }
            catch (e) {}
          }
        }

        return Promise.all(asyncDraws).then(function() {
          try { return c.toDataURL('image/png'); }
          catch (e) {
            console.error('toDataURL failed (canvas tainted):', e);
            throw e;
          }
        });
      });
    }

    var overlay = document.createElement('div');
    overlay.className = 'outfit-cfg__share-preview';

    var card = document.createElement('div');
    card.className = 'outfit-cfg__share-preview-card outfit-cfg__share-popup';

    var title = document.createElement('p');
    title.className = 'outfit-cfg__share-preview-title';
    title.textContent = 'Wo möchten Sie teilen?';
    card.appendChild(title);

    var img = document.createElement('img');
    img.src = dataUrl;
    img.className = 'outfit-cfg__share-preview-img';
    card.appendChild(img);

    var grid = document.createElement('div');
    grid.className = 'outfit-cfg__share-grid';
    card.appendChild(grid);

    function close() { overlay.remove(); document.body.style.overflow = ''; }

    function makeDestButton(opts) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'outfit-cfg__share-dest outfit-cfg__share-dest--' + opts.kind;
      btn.innerHTML = '<span class="outfit-cfg__share-dest-icon">' + opts.icon + '</span><span class="outfit-cfg__share-dest-label">' + opts.label + '</span>';
      btn.addEventListener('click', function() {
        if (opts.busyText) {
          var labelEl = btn.querySelector('.outfit-cfg__share-dest-label');
          if (labelEl) labelEl.textContent = opts.busyText;
          btn.disabled = true;
        }
        try { opts.onClick(btn); } catch (e) { console.error('Share destination failed:', e); }
      });
      return btn;
    }

    // Facebook — share the OG-aware URL so FB scrapes the outfit screenshot
    // for the link preview. og:url points at goessl.com so the visible domain
    // in the post is "goessl.com".
    grid.appendChild(makeDestButton({
      kind: 'facebook', icon: SHARE_ICONS.facebook, label: 'Facebook',
      busyText: 'Bild wird hochgeladen…',
      onClick: function(btn) {
        getOgUrl().then(function(ogUrl) {
          var fb = 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(ogUrl);
          window.open(fb, '_blank', 'noopener,noreferrer,width=640,height=520');
          var labelEl = btn.querySelector('.outfit-cfg__share-dest-label');
          if (labelEl) labelEl.textContent = 'Facebook';
          btn.disabled = false;
        }).catch(function() {
          var labelEl = btn.querySelector('.outfit-cfg__share-dest-label');
          if (labelEl) labelEl.textContent = 'Fehler';
          btn.disabled = false;
        });
      }
    }));

    // Pinterest — pin creator with image + OG-aware URL. Pinterest crawls
    // url= for additional metadata, so the OG endpoint gives them a clean
    // og:image + goessl.com og:url even though the actual click goes through
    // gsl.asksj.com first.
    grid.appendChild(makeDestButton({
      kind: 'pinterest', icon: SHARE_ICONS.pinterest, label: 'Pinterest',
      busyText: 'Bild wird hochgeladen…',
      onClick: function(btn) {
        Promise.all([getR2Url(), getOgUrl()]).then(function(vals) {
          var imageUrl = vals[0], ogUrl = vals[1];
          var pin = 'https://www.pinterest.com/pin/create/button/'
            + '?url=' + encodeURIComponent(ogUrl)
            + '&media=' + encodeURIComponent(imageUrl)
            + '&description=' + encodeURIComponent('Mein Gössl Outfit');
          window.open(pin, '_blank', 'noopener,noreferrer,width=720,height=620');
          var labelEl = btn.querySelector('.outfit-cfg__share-dest-label');
          if (labelEl) labelEl.textContent = 'Pinterest';
          btn.disabled = false;
        }).catch(function() {
          var labelEl = btn.querySelector('.outfit-cfg__share-dest-label');
          if (labelEl) labelEl.textContent = 'Fehler';
          btn.disabled = false;
        });
      }
    }));

    // Instagram — IG can't accept inbound shares from the web. On mobile with
    // navigator.share + file, IG appears in the system share sheet. On desktop,
    // we save the image and prompt the user to upload it manually.
    grid.appendChild(makeDestButton({
      kind: 'instagram', icon: SHARE_ICONS.instagram, label: 'Instagram',
      busyText: 'Wird vorbereitet…',
      onClick: function(btn) {
        var labelEl = btn.querySelector('.outfit-cfg__share-dest-label');
        Promise.all([getFile(), shortUrlPromise]).then(function(vals) {
          var file = vals[0], shortUrl = vals[1];
          if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
            return navigator.share({
              title: 'Mein Gössl Outfit',
              text: buildShareText(shortUrl),
              files: [file]
            }).then(function() { close(); }).catch(function(err) {
              if (labelEl) labelEl.textContent = 'Instagram';
              btn.disabled = false;
              if (err && err.name === 'AbortError') return;
            });
          }
          // Desktop fallback — save image and prompt user to upload manually.
          return getRealDataUrl().then(function(du) {
            triggerDownload(du, 'goessl-outfit.png');
            if (labelEl) labelEl.textContent = 'Bild gespeichert · auf Instagram hochladen';
            setTimeout(function() {
              if (labelEl) labelEl.textContent = 'Instagram';
              btn.disabled = false;
            }, 3500);
          });
        }).catch(function() {
          if (labelEl) labelEl.textContent = 'Fehler';
          btn.disabled = false;
        });
      }
    }));

    // Save image — local PNG download. Uses the lazy native-canvas
    // capture so iOS gets a real screenshot the same way Pinterest +
    // Instagram do.
    grid.appendChild(makeDestButton({
      kind: 'download', icon: SHARE_ICONS.download, label: 'Bild speichern',
      busyText: 'Wird erstellt …',
      onClick: function(btn) {
        var labelEl = btn.querySelector('.outfit-cfg__share-dest-label');
        getRealDataUrl().then(function(du) {
          triggerDownload(du, 'goessl-outfit.png');
          if (labelEl) labelEl.textContent = 'Gespeichert ✓';
          setTimeout(function() {
            if (labelEl) labelEl.textContent = 'Bild speichern';
            btn.disabled = false;
          }, 2000);
        }).catch(function(err) {
          console.error('Save failed:', err);
          if (labelEl) labelEl.textContent = 'Fehler';
          setTimeout(function() {
            if (labelEl) labelEl.textContent = 'Bild speichern';
            btn.disabled = false;
          }, 2500);
        });
      }
    }));

    // Copy link — copies the share text + goessl.com URL together so when
    // the user pastes into a chat/post they get a captioned preview ready.
    grid.appendChild(makeDestButton({
      kind: 'link', icon: SHARE_ICONS.link, label: 'Link kopieren',
      busyText: 'Wird vorbereitet…',
      onClick: function(btn) {
        if (!navigator.clipboard) return;
        var labelEl = btn.querySelector('.outfit-cfg__share-dest-label');
        shortUrlPromise.then(function(shortUrl) {
          return navigator.clipboard.writeText(buildShareText(shortUrl));
        }).then(function() {
          if (labelEl) labelEl.textContent = 'Kopiert ✓';
          setTimeout(function() {
            if (labelEl) labelEl.textContent = 'Link kopieren';
            btn.disabled = false;
          }, 2000);
        }).catch(function() {
          if (labelEl) labelEl.textContent = 'Fehler';
          btn.disabled = false;
        });
      }
    }));

    var cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'outfit-cfg__share-cancel';
    cancel.textContent = 'Abbrechen';
    cancel.addEventListener('click', close);
    card.appendChild(cancel);

    overlay.appendChild(card);
    overlay.addEventListener('click', function(e) {
      if (e.target === overlay) close();
    });
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
  }

  function triggerDownload(dataUrl, filename) {
    var a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }


  function captureAndUpload() {
    var canvas = document.querySelector('[data-outfit-canvas]');
    if (!canvas) return Promise.reject('No canvas');

    // Temporarily unfix canvas for html2canvas
    var wrap = canvas.closest('.outfit-cfg__canvas-wrap');
    var savedPosition = wrap ? wrap.style.cssText : '';
    if (wrap && getComputedStyle(wrap).position === 'fixed') {
      wrap.style.position = 'absolute';
      wrap.style.bottom = 'auto';
      wrap.style.top = '0';
      wrap.style.left = '0';
      wrap.style.width = wrap.offsetWidth + 'px';
      wrap.style.height = wrap.offsetHeight + 'px';
      // Move offscreen to hide during capture. Avoid `opacity` — html2canvas
      // honors computed opacity and would bake it into the rendered image,
      // producing a faded/transparent screenshot.
      wrap.style.left = '-100000px';
      wrap.style.zIndex = '-1';
    }

    return loadHtml2Canvas().then(function() {
      return html2canvas(canvas, { useCORS: true, allowTaint: true, scale: 2, backgroundColor: null });
    }).then(function(rendered) {
      if (wrap) wrap.style.cssText = savedPosition;
      var dataUrl = rendered.toDataURL('image/png');
      return fetch(R2_UPLOAD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: dataUrl })
      });
    }).then(function(r) { return r.json(); }).then(function(data) {
      if (data.url) return data.url;
      throw new Error(data.error || 'Upload failed');
    });
  }

  function buildShareText(shareUrl) {
    var lines = ['Mein G\u00F6ssl Outfit\n'];
    getState().forEach(function(item) {
      var product = productCache[item.handle];
      if (!product) return;
      // Extract group_id from handle (first segment before dash)
      var groupId = item.handle.split('-')[0];
      lines.push('\u2022 ' + product.title + ' (Art. ' + groupId + ')');
    });
    // Caller must pass the gsl.asksj.com short URL \u2014 pasting the long
    // ?l=\u2026 URL into chat clients (WhatsApp, Messenger, SMS) gets truncated
    // mid-base64 and the recipient lands on a partial outfit. mintShortUrl
    // already falls back to the long URL if the worker is unreachable, so
    // a missing argument here would defeat the purpose.
    lines.push('\nOutfit ansehen: ' + (shareUrl || getShareUrl()));
    return lines.join('\n');
  }

  function shareImage(imageUrl) {
    return mintShortUrl(getShareUrl()).then(function(outfitUrl) {
    if (navigator.share && navigator.canShare) {
      return fetch(imageUrl).then(function(r) { return r.blob(); }).then(function(blob) {
        var file = new File([blob], 'goessl-outfit.png', { type: 'image/png' });
        var shareData = { title: 'Mein G\u00F6ssl Outfit', text: 'Schau dir mein Outfit an! ' + outfitUrl, files: [file] };
        if (navigator.canShare(shareData)) return navigator.share(shareData);
        return navigator.share({ title: 'Mein G\u00F6ssl Outfit', url: outfitUrl });
      }).catch(function() {});
    }
    if (navigator.clipboard) {
      navigator.clipboard.writeText(imageUrl);
      if (shareBtn) { shareBtn.textContent = 'Bild-Link kopiert!'; setTimeout(function() { shareBtn.textContent = 'Teilen'; }, 2000); }
    }
    window.open(imageUrl, '_blank');
    return Promise.resolve();
    });
  }

  var html2canvasLoaded = false;
  function loadHtml2Canvas() {
    if (html2canvasLoaded) return Promise.resolve();
    return new Promise(function(resolve, reject) {
      var s = document.createElement('script');
      // html2canvas-pro: actively maintained fork with iOS Safari color
      // management fixes (oklch, alpha blending, CSS backdrop-filter).
      // jsdelivr is in our CSP allowlist; cdnjs is not.
      s.src = 'https://cdn.jsdelivr.net/npm/html2canvas-pro@1.5.13/dist/html2canvas-pro.min.js';
      s.onload = function() { html2canvasLoaded = true; resolve(); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  // ═══════════════════════════════════════════
  //  PUBLIC API
  // ═══════════════════════════════════════════

  window.Goessl = window.Goessl || {};
  window.Goessl.configurator = {
    getShareUrl: getShareUrl,
    getState: function() { return getState(); },
    getCurrentGender: function() { return currentGender; },
    resetPositions: resetPositions,
    clearCanvas: clearCanvas,
    // Public helpers for the quick-view modal so it can drop a product onto the canvas
    selectProduct: function(product, variant) {
      var collection = guessCollectionForProduct(product);
      var colorCode = null;
      if (variant && variant.option1) colorCode = extractColorCode(variant.option1);
      // Cache so renderCanvas can find images
      productCache[product.handle] = product;
      selectProduct(product, collection, colorCode);
      if (variant && variant.id) {
        var s = getState();
        for (var i = 0; i < s.length; i++) {
          if (s[i].handle === product.handle) { s[i].variant_id = variant.id; break; }
        }
      }
      updateUI();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
